// src/screens/EventWatchScreen.js
// Live Event Watch Screen - Video-first layout with chat drawer, bracket modal, and watermark

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  ScrollView,
  TextInput,
  StyleSheet,
  useWindowDimensions,
  SafeAreaView,
  ActivityIndicator,
  Modal,
  Animated,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { db, auth } from '../../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { WebView } from 'react-native-webview';

import TsunamiMeter, {
  RumbleBurst,
  claimRumbleMilestone,
  useRumbleSetup,
  eventRumbleTotals,
  rumbleMeterLevel,
  rumbleRowWave,
} from '../components/TsunamiMeter';
import { waveLabel } from '../utils/rumblePayment';
import LiveHlsPlayer from '../components/LiveHlsPlayer';
import { fmtCrits, fmtCompactCrits } from '../utils/currencyHelper';
import useCritsEnabled from '../hooks/useCritsEnabled';
import useEventCeremony from '../hooks/useEventCeremony';
import CeremonyStage from '../components/ceremony/CeremonyStage';

// Viewport-derived layout is READ THROUGH useWindowDimensions inside the
// component, never captured at module scope. Module-scope Dimensions.get() is
// evaluated once at import: it survives resize, rotation and fullscreen
// transitions unchanged, and anything baked into StyleSheet.create() from it is
// frozen for the life of the tab.
//
// Breakpoints only — no measurements here.
const WIDE_BREAKPOINT = 1024;   // side-by-side video + chat above this
const CHAT_WIDTH_WIDE = 340;
const CHAT_WIDTH_NARROW = 300;
// Floor for the video column — the chat column yields rather than crush it.
const MIN_VIDEO_WIDTH = 320;

// Height reserved at the bottom of the player for the compact info bar, so the
// overlay's bottom control row sits ABOVE it instead of underneath. Keep in sync
// with styles.compactBar (paddingVertical 10 x2 + 40px button = 60).
const COMPACT_BAR_HEIGHT = 60;

// Event Rumbles (evergreen 3.3; the "flood" names are historical). A Rumble is
// a real-money purchase FROM CritCash that boosts a live event; once results
// are final CritCash credits a share to the verified players who played, and
// it is refunded if the event is cancelled, nobody can receive, or it never
// finishes. Payment happens ON-SCREEN (saved-card one-tap, or an embedded
// Stripe checkout) so the viewer is never pulled off the live stream. The
// button shows only while setup says Rumbles are open for this viewer + event
// (server flag settings/streams.eventRumblesEnabled, live, before the close
// time); fan-facing copy is money-free (counts and waves, never amounts).
const FLOOD_ENABLED = true;

// Firebase Storage bucket
const FIREBASE_STORAGE_BUCKET = 'critcash-45968.firebasestorage.app';

// CritCash HLS Server (REMOVED - Mux CDN is now the sole playback path)
// const HLS_SERVER = 'https://play.critcash.com';

// CritCash watermark URL
const WATERMARK_URL = `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_STORAGE_BUCKET}/o/assets%2Fwatermarks%2Fcritcash_logo_transparent.png?alt=media`;

// ============================================
// DEMO MODE
// When enabled, demo events (seeded by seedDemoData.js) get a scripted
// auto-playing chat simulation. All other data loads from Firestore.
// ============================================
const DEMO_MODE = true;
const DEMO_EVENT_PREFIX = 'ycdemo_ev_';

// Timing for scripted chat auto-play (ms)
const DEMO_CHAT_INTERVAL_BASE = 2500;
const DEMO_CHAT_INTERVAL_VARIANCE = 1500;

// Scripted chat messages that auto-play in demo mode
const DEMO_CHAT_SCRIPT = [
  { type: 'chat', user: 'FGC_Demon', message: 'LETS GOOO' },
  { type: 'chat', user: 'StreamViewer88', message: 'this match is insane already' },
  { type: 'chat', user: 'ProGrinder', message: 'that read was disgusting' },
  { type: 'flood', user: 'CDLWatcher', message: 'sent a Rumble!', amount: 5000 },
  { type: 'chat', user: 'TourneyFan', message: 'crowd going crazy rn' },
  { type: 'chat', user: 'CompetitorX', message: 'the spacing is elite' },
  { type: 'system', message: 'Round 2 starting!' },
  { type: 'chat', user: 'GreenWall', message: 'LETS GO BABY' },
  { type: 'chat', user: 'WatchParty_', message: 'I called this matchup preseason' },
  { type: 'chat', user: 'FGC_OG', message: 'been watching since pools and this is the best set today' },
  { type: 'flood', user: 'CoD_Legends', message: 'sent a Rumble!', amount: 25000 },
  { type: 'chat', user: 'CritCashFan', message: 'the crowd meter keeps climbing lol' },
  { type: 'chat', user: 'Ranked_Andy', message: 'what a combo' },
  { type: 'chat', user: 'eSportsDaily', message: 'this is clip worthy for sure' },
  { type: 'chat', user: 'SaltMiner', message: 'how do you even react to that' },
  { type: 'system', message: 'Rumble! The crowd is shaking...' },
  { type: 'chat', user: 'LobbyLegend', message: 'GGs in the chat' },
  { type: 'chat', user: 'StreamViewer88', message: 'the pressure is unreal' },
  { type: 'flood', user: 'OpTicFan2024', message: 'sent a Rumble!', amount: 10000 },
  { type: 'chat', user: 'BracketBuster', message: 'upset incoming??' },
  { type: 'chat', user: 'FGC_Demon', message: 'no way no way no way' },
  { type: 'chat', user: 'CompetitorX', message: 'the adaptation mid-set is wild' },
  { type: 'chat', user: 'ProGrinder', message: 'that was CLEAN' },
  { type: 'chat', user: 'TourneyFan', message: 'CritCash tournaments are built different' },
  { type: 'system', message: 'Match point!' },
  { type: 'chat', user: 'WatchParty_', message: 'my hands are sweating and Im just watching' },
  { type: 'chat', user: 'FGC_OG', message: 'this is top 5 sets of the year easy' },
  { type: 'flood', user: 'FaZeUp', message: 'sent a Rumble!', amount: 2500 },
  { type: 'chat', user: 'Ranked_Andy', message: 'thats why you dont sleep on the lower seed' },
  { type: 'chat', user: 'CritCashFan', message: 'the tsunami meter about to pop' },
  { type: 'chat', user: 'SaltMiner', message: 'whoever takes this deserves it' },
  { type: 'chat', user: 'LobbyLegend', message: 'W tournament W vibes' },
  { type: 'chat', user: 'GreenWall', message: 'SOMEBODY CLIP THAT' },
  { type: 'chat', user: 'eSportsDaily', message: 'adding this to the highlight reel for sure' },
  { type: 'flood', user: 'ProGamer99', message: 'sent a Rumble!', amount: 7500 },
  { type: 'chat', user: 'BracketBuster', message: 'the comeback is real' },
  { type: 'chat', user: 'StreamViewer88', message: 'I cant look away' },
  { type: 'system', message: 'Final round!' },
  { type: 'chat', user: 'FGC_Demon', message: 'HOLD THAT' },
  { type: 'chat', user: 'CompetitorX', message: 'the mental game is so strong here' },
  { type: 'chat', user: 'TourneyFan', message: 'grand finals gonna be even crazier' },
  { type: 'chat', user: 'ProGrinder', message: 'we are witnessing greatness' },
  { type: 'chat', user: 'WatchParty_', message: 'gg gg' },
  { type: 'flood', user: 'TsunamiKing', message: 'sent a Rumble!', amount: 15000 },
  { type: 'chat', user: 'Ranked_Andy', message: 'crowd meter is FULL' },
  { type: 'chat', user: 'FGC_OG', message: 'remember this day' },
  { type: 'chat', user: 'CritCashFan', message: 'love this platform honestly' },
  { type: 'chat', user: 'SaltMiner', message: 'heartbreaker for the loser though' },
  { type: 'chat', user: 'LobbyLegend', message: 'next match when??' },
  { type: 'chat', user: 'GreenWall', message: 'INJECT THIS INTO MY VEINS' },
];

// ---------- Helpers ----------
function formatDollars(cents) {
  const dollars = (cents || 0) / 100;
  if (dollars >= 1000000) return `$${(dollars / 1000000).toFixed(2)}M`;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Rumble activity is money-free on this screen: a count (Rumbles) and wave
// tiers, never an amount — money NEVER appears on public surfaces.
function formatRumbleCount(n) {
  const c = Math.max(0, Math.floor(Number(n) || 0));
  if (c >= 1000000) return `${(c / 1000000).toFixed(1)}M`;
  if (c >= 1000) return `${(c / 1000).toFixed(1)}K`;
  return String(c);
}

// Currency-aware compact formatter for an event's BASE prize pool. Crit events
// carry their pool in whole Crits (1 Crit = $1) on potCents/netPoolCents; cash
// events carry cents. Flood amounts are NEVER routed through here — flood is a
// separate real-money (USD) stream (viewer boosts → monthly creator comp), shown
// on its own and always in $, so it must not be mixed into the Crit prize pool.
function formatEventPrize(event, amount) {
  return event?.isCritEvent ? fmtCompactCrits(amount) : formatDollars(amount);
}

function getEmbedUrl(stream) {
  if (!stream) return null;
  
  if (stream.provider === 'youtube' && stream.videoId) {
    // Parse the videoId to extract clean ID and any time parameter
    let videoId = stream.videoId;
    let startTime = stream.startTime || null;
    
    // Handle videoIds that incorrectly include URL parameters like "ABC123&t=195"
    if (videoId.includes('&')) {
      const parts = videoId.split('&');
      videoId = parts[0]; // Get the clean video ID
      
      // Extract time parameter if present
      for (let i = 1; i < parts.length; i++) {
        const param = parts[i];
        if (param.startsWith('t=')) {
          startTime = param.substring(2);
        }
      }
    }
    
    // Also handle "ABC123?t=195" format
    if (videoId.includes('?')) {
      const parts = videoId.split('?');
      videoId = parts[0];
      const queryStr = parts[1] || '';
      const params = new URLSearchParams(queryStr);
      if (params.has('t')) {
        startTime = params.get('t');
      }
    }
    
    // Build the embed URL with proper parameters
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://critcash.com';
    let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&enablejsapi=1&origin=${encodeURIComponent(origin)}`;
    
    // Add start time if present (YouTube uses 'start' parameter for embeds)
    if (startTime) {
      embedUrl += `&start=${startTime}`;
    }
    
    return embedUrl;
  }
  
  if (stream.provider === 'twitch' && stream.channelName) {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    return `https://player.twitch.tv/?channel=${stream.channelName}&parent=${hostname}&muted=true&autoplay=true`;
  }
  
  // CritCash HLS stream - return the playback URL directly
  if (stream.provider === 'critcash' && stream.playbackUrl) {
    return stream.playbackUrl;
  }
  
  // No fallback to play.critcash.com — Mux CDN provides the playback URL
  return null;
}

// Pick which participant the viewer should be watching after the participant list
// is rebuilt. Called from the event-doc snapshot handlers, which re-resolve the
// whole roster on every write to the event document (a flood/Super Crit, a viewer
// count bump, a round advance).
//
// MUST be used with the functional form of setSelectedStream: the snapshot
// callbacks close over state from subscription time, so reading `selectedStream`
// directly there always sees the initial null and snaps the viewer back to the
// first racer on every write.
//
// Object identity matters as much as the choice: returning a fresh object each
// time remounts the <video> element and restarts the stream. Hold the previous
// object while the feed is unchanged.
// Identity of a feed, ignoring the query string. Mux mints a FRESH signed token
// on every resolve, so comparing whole URLs reports "changed" every single time
// and would remount the <video> on every flood write.
function streamIdentity(stream) {
  const url = getEmbedUrl(stream);
  return url ? url.split('?')[0] : null;
}

// How many entry docs to read for an event. These reads only supply display
// names and avatars, so truncating degrades gracefully (getEventStreams returns
// its own display names) — but a flat limit(50) silently cut real rosters:
// BR caps at 64, racing runs gridSize x heatCount (GT7: 240), and the create
// flow offers brackets far past that. Bound by the event's own capacity, with a
// ceiling so a huge bracket can't melt the client.
function entriesReadCap(eventData) {
  const declared = Number(eventData?.maxEntrants || eventData?.bracketSize || 0) || 0;
  return Math.min(512, Math.max(64, declared));
}

// Fold a fresh getEventStreams result into the current participant list.
//
// Returns the SAME array when nothing meaningfully changed, so a poll that finds
// the identical feeds costs zero re-renders. Display data resolved from entries
// and public_profiles is richer than the callable's, so it is preserved.
//
// Additive only: participants are not removed here. The callable can transiently
// miss a racer, and dropping them would make the POV picker flicker; genuine
// departures are still reconciled by the event-doc resolve path.
//
// Known limitation: while a feed's identity is unchanged we hold the original
// signed URL, so a spectator watching one racer for >2h outlives the Mux token
// and needs a reload. Not worth a remount-per-poll to avoid.
function mergeLiveStreams(prev, streams) {
  const byId = new Map((prev || []).map((p) => [p.odUserId, p]));
  let changed = false;

  for (const s of streams) {
    if (!s?.odUserId) continue;
    const stream = {
      provider: 'critcash',
      playbackUrl: s.playbackUrl,
      source: s.source,
      thumbnailUrl: s.thumbnailUrl || null,
    };
    const existing = byId.get(s.odUserId);

    if (!existing) {
      byId.set(s.odUserId, {
        odUserId: s.odUserId,
        displayName: s.displayName || s.odUserId,
        avatarUrl: null,
        stream,
      });
      changed = true;
    } else if (streamIdentity(stream) !== streamIdentity(existing.stream)) {
      // The feed genuinely came up or moved between the relay and Mux.
      byId.set(s.odUserId, { ...existing, stream });
      changed = true;
    }
  }

  return changed ? Array.from(byId.values()) : prev;
}

function pickSelectedStream(prev, liveParticipants, allParticipants = liveParticipants) {
  if (!prev) return liveParticipants[0] || null;

  const fresh = liveParticipants.find((p) => p.odUserId === prev.odUserId);
  if (fresh) {
    // Same racer, same feed: hold the existing object so hls.js keeps playing.
    // The signed token we hold stays valid well past a single event.
    return streamIdentity(fresh.stream) === streamIdentity(prev.stream) ? prev : fresh;
  }

  // Chosen racer didn't resolve this cycle. If they're still an entrant it's a
  // transient miss (callable hiccup, flappy mux status) — keep playing what we
  // have rather than snapping the viewer to somebody else.
  if (allParticipants.some((p) => p.odUserId === prev.odUserId)) return prev;

  // Genuinely gone (quit, feed ended). Fall back, but never to nothing if the
  // roster is momentarily empty.
  return liveParticipants[0] || prev;
}

// EmbedPlayer Component - Uses native iframe on web for better YouTube/Twitch compatibility
const EmbedPlayer = ({ embedUrl, provider }) => {
  if (Platform.OS === 'web') {
    // Use native iframe on web - WebView doesn't handle YouTube embeds well
    return (
      <View style={styles.videoPlayer}>
        <iframe
          src={embedUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </View>
    );
  }
  
  // Use WebView on native platforms (iOS/Android)
  return (
    <WebView
      source={{ uri: embedUrl }}
      style={styles.videoPlayer}
      allowsFullscreenVideo
      mediaPlaybackRequiresUserAction={false}
      javaScriptEnabled={true}
      domStorageEnabled={true}
      allowsInlineMediaPlayback={true}
    />
  );
};

// The CritCash HLS player lives in components/LiveHlsPlayer.js: a
// double-buffered <video> pair that preloads the incoming POV invisibly,
// aligns it to the outgoing feed's race clock, and only swaps once a frame
// has painted - so switching cameras never shows black.

function isDemoEvent(eventId) {
  return DEMO_MODE && eventId && eventId.startsWith(DEMO_EVENT_PREFIX);
}

function timeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

// ---------- Chat Panel Component (inline, not overlay) ----------
const ChatPanel = ({ visible, onClose, eventId, eventTitle, isDemo, isLoggedIn, onRequireAuth, onUserPress, hiddenUids, width, stacked }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sender, setSender] = useState(null); // { name, photoUrl } for the logged-in user
  const scrollRef = useRef(null);
  // Stick-to-bottom latch. The Firestore subscription caps at 100 docs (demo
  // mode rolls an 80-message window), so once the room is busy
  // messages.length stops changing — following must key off message identity
  // and content size, never off length. True = follow new messages; flips
  // false only when the viewer deliberately scrolls up.
  const pinnedToBottomRef = useRef(true);
  const lastMsgIdRef = useRef(null);
  const [hasUnseen, setHasUnseen] = useState(false);
  const scriptIndexRef = useRef(0);
  const scriptTimerRef = useRef(null);

  // Resolve the logged-in user's PUBLIC PROFILE name once, so their chat
  // messages are attributed to them (the old code hardcoded "Anonymous").
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) { setSender(null); return; }
    let active = true;
    // NEVER fall back to auth displayName on this public surface — for real
    // accounts that's the person's legal name (public-name rule, a26f577).
    setSender({ name: 'Player', photoUrl: u.photoURL || null });
    (async () => {
      try {
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'public_profiles', u.uid));
        if (active && snap.exists()) {
          const d = snap.data();
          setSender({
            name: d.userName || 'Player',
            photoUrl: d.profilePhotoUrl || u.photoURL || null,
          });
        }
      } catch (e) { /* keep the safe fallback */ }
    })();
    return () => { active = false; };
  }, [isLoggedIn]);

  // Demo chat auto-play: push scripted messages on a randomized timer
  useEffect(() => {
    if (!isDemo || !visible) {
      // Stop the script timer when hidden or not demo
      if (scriptTimerRef.current) {
        clearTimeout(scriptTimerRef.current);
        scriptTimerRef.current = null;
      }
      return;
    }

    // Seed a few initial messages so chat doesn't look empty on open
    const initialBatch = DEMO_CHAT_SCRIPT.slice(0, 5).map((msg, i) => ({
      ...msg,
      id: `demo-init-${i}`,
      timestamp: Date.now() - (5 - i) * 3000,
    }));
    setMessages(initialBatch);
    scriptIndexRef.current = 5;

    // Schedule the next scripted message
    const scheduleNext = () => {
      const delay = DEMO_CHAT_INTERVAL_BASE + (Math.random() * DEMO_CHAT_INTERVAL_VARIANCE * 2 - DEMO_CHAT_INTERVAL_VARIANCE);
      scriptTimerRef.current = setTimeout(() => {
        const idx = scriptIndexRef.current % DEMO_CHAT_SCRIPT.length;
        const scriptMsg = DEMO_CHAT_SCRIPT[idx];
        const newMsg = {
          ...scriptMsg,
          id: `demo-${Date.now()}-${idx}`,
          timestamp: Date.now(),
        };
        setMessages(prev => {
          const updated = [...prev, newMsg];
          // Rolling window - keep the last 80 messages
          return updated.length > 80 ? updated.slice(-80) : updated;
        });
        scriptIndexRef.current = idx + 1;
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => {
      if (scriptTimerRef.current) {
        clearTimeout(scriptTimerRef.current);
        scriptTimerRef.current = null;
      }
    };
  }, [isDemo, visible]);

  // Real-time Firestore chat subscription for non-demo events
  useEffect(() => {
    if (isDemo || !eventId) return;

    const chatUnsub = onSnapshot(
      query(
        collection(db, 'events', eventId, 'chat'),
        orderBy('timestamp', 'desc'),
        limit(100)
      ),
      (snap) => {
        const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
        setMessages(msgs);
      }
    );

    return () => chatUnsub();
  }, [eventId, isDemo]);

  // Follow the newest message only while pinned to the bottom. A viewer who
  // scrolled up keeps their place and gets a "New messages" chip instead of
  // being yanked back down.
  useEffect(() => {
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    if (!lastId || lastId === lastMsgIdRef.current) return;
    lastMsgIdRef.current = lastId;
    if (pinnedToBottomRef.current) {
      // Non-animated: under rapid appends an animated scroll can be cut short
      // mid-flight and settle above the bottom.
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 50);
    } else {
      setHasUnseen(true);
    }
  }, [messages]);

  const handleChatScroll = (e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    // Slop absorbs wrapped rows and sub-pixel rounding — only a real scroll-up
    // counts as reading history.
    const atBottom = contentSize.height - contentOffset.y - layoutMeasurement.height <= 48;
    pinnedToBottomRef.current = atBottom;
    if (atBottom) setHasUnseen(false);
  };

  // Late layout growth (text wrap, images finishing) re-sticks a pinned view;
  // this also covers appends whose row heights offset exactly.
  const handleChatContentSize = () => {
    if (pinnedToBottomRef.current) scrollRef.current?.scrollToEnd({ animated: false });
  };

  const jumpToLatest = () => {
    pinnedToBottomRef.current = true;
    setHasUnseen(false);
    scrollRef.current?.scrollToEnd({ animated: true });
  };

  const handleSend = async () => {
    if (!inputText.trim()) return;

    if (isDemo) {
      // Add user message inline with the scripted chat
      const newMsg = {
        id: `user-${Date.now()}`,
        type: 'chat',
        user: 'You',
        message: inputText.trim(),
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, newMsg]);
      setInputText('');
      return;
    }

    // Real send — attribute the message to the logged-in user.
    const u = auth.currentUser;
    if (!u) { onRequireAuth?.(); return; }
    try {
      await addDoc(collection(db, 'events', eventId, 'chat'), {
        type: 'chat',
        // Public handle only — never the auth displayName (real-name leak).
        user: sender?.name || 'Player',
        userId: u.uid,
        photoUrl: sender?.photoUrl || null,
        message: inputText.trim(),
        timestamp: serverTimestamp(),
      });
      setInputText('');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const renderMessage = (msg) => {
    // No special card for type:'flood' any more: no server ever wrote one to
    // event chat, and the open create rule let anyone forge "boosted N Crits"
    // cards next to real cash Rumbles. Such lines (and the demo script's)
    // fall through to the plain renderer below, money-free.
    if (msg.type === 'system') {
      return (
        <View key={msg.id} style={styles.chatMsgSystem}>
          <Text style={styles.chatMsgSystemText}>{msg.message}</Text>
        </View>
      );
    }

    return (
      <View key={msg.id} style={styles.chatMsg}>
        {/* Handle → user card (profile / hide / report). Demo/scripted
            messages carry no userId and stay inert. */}
        <Text
          style={styles.chatMsgUser}
          onPress={onUserPress && msg.userId ? () => onUserPress(msg) : undefined}
        >
          {msg.user}
        </Text>
        <Text style={styles.chatMsgText}>{msg.message}</Text>
      </View>
    );
  };

  if (!visible) return null;

  return (
    <View style={[styles.chatPanel, stacked && styles.chatPanelStacked, { width }]}>
      {/* Chat Header */}
      <View style={styles.chatHeader}>
        <View>
          <Text style={styles.chatTitle}>Event Chat</Text>
          <Text style={styles.chatSubtitle}>{eventTitle}</Text>
        </View>
        <Pressable style={styles.chatCloseBtn} onPress={onClose}>
          <Text style={styles.chatCloseBtnText}>X</Text>
        </Pressable>
      </View>

      {/* Chat Messages */}
      <View style={{ flex: 1, minHeight: 0 }}>
        <ScrollView
          ref={scrollRef}
          style={styles.chatMessages}
          contentContainerStyle={styles.chatMessagesContent}
          onScroll={handleChatScroll}
          scrollEventThrottle={16}
          onContentSizeChange={handleChatContentSize}
        >
          {messages
            .filter((m) => !(hiddenUids && m.userId && hiddenUids.has(m.userId)))
            .map(renderMessage)}
        </ScrollView>
        {hasUnseen ? (
          <Pressable style={styles.chatNewMsgs} onPress={jumpToLatest} accessibilityLabel="Jump to newest messages">
            <Text style={styles.chatNewMsgsText}>New messages</Text>
          </Pressable>
        ) : null}
      </View>

      {/* Chat Input — gated: logged-out spectators get a sign-up funnel instead of the input */}
      {isLoggedIn ? (
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.chatInputContainer}
        >
          <TextInput
            style={styles.chatInput}
            placeholder="Send a message..."
            placeholderTextColor="#666"
            value={inputText}
            onChangeText={setInputText}
            onSubmitEditing={handleSend}
            returnKeyType="send"
          />
          <Pressable
            style={[styles.chatSendBtn, !inputText.trim() && styles.chatSendBtnDisabled]}
            onPress={handleSend}
            disabled={!inputText.trim()}
          >
            <Image source={PURPLE_DIE_ICON} style={styles.chatSendDie} resizeMode="contain" />
          </Pressable>
        </KeyboardAvoidingView>
      ) : (
        <Pressable style={styles.chatSignupCta} onPress={onRequireAuth}>
          <Text style={styles.chatSignupCtaText}>Sign up to chat</Text>
          <Text style={styles.chatSignupCtaSub}>Watching is free — create an account to join the chat</Text>
        </Pressable>
      )}
    </View>
  );
};

// ---------- Bracket Modal with Connecting Lines ----------
const MATCH_HEIGHT = 56; // Height of each match box
const MATCH_WIDTH = 140; // Width of each match box
const CONNECTOR_WIDTH = 24; // Width of horizontal connector lines
const ROUND_GAP = CONNECTOR_WIDTH * 2; // Gap between rounds (includes connectors)

const BracketMatch = ({ match, onPress, isLive }) => {
  const p1Winner = match.player1?.winner;
  const p2Winner = match.player2?.winner;
  const isCompleted = match.status === 'completed';
  const isUpcoming = match.status === 'upcoming';
  
  return (
    <Pressable
      style={[
        styles.bracketMatchBox,
        isLive && styles.bracketMatchBoxLive,
        isUpcoming && styles.bracketMatchBoxUpcoming,
      ]}
      onPress={() => onPress(match)}
    >
      {/* Player 1 */}
      <View style={[styles.bracketPlayerRow, p1Winner && styles.bracketPlayerRowWinner]}>
        <Text 
          style={[styles.bracketPlayerName, p1Winner && styles.bracketPlayerNameWinner]} 
          numberOfLines={1}
        >
          {match.player1?.name || 'TBD'}
        </Text>
        <Text style={[styles.bracketPlayerScore, p1Winner && styles.bracketPlayerScoreWinner]}>
          {isCompleted ? (match.player1?.score ?? 0) : '-'}
        </Text>
      </View>
      {/* Divider */}
      <View style={styles.bracketMatchBoxDivider} />
      {/* Player 2 */}
      <View style={[styles.bracketPlayerRow, p2Winner && styles.bracketPlayerRowWinner]}>
        <Text 
          style={[styles.bracketPlayerName, p2Winner && styles.bracketPlayerNameWinner]} 
          numberOfLines={1}
        >
          {match.player2?.name || 'TBD'}
        </Text>
        <Text style={[styles.bracketPlayerScore, p2Winner && styles.bracketPlayerScoreWinner]}>
          {isCompleted ? (match.player2?.score ?? 0) : '-'}
        </Text>
      </View>
      {/* Live indicator */}
      {isLive && (
        <View style={styles.bracketLiveIndicator}>
          <View style={styles.bracketLiveDot} />
          <Text style={styles.bracketLiveText}>LIVE</Text>
        </View>
      )}
    </Pressable>
  );
};

// Connector lines between matches
const BracketConnector = ({ matchIndex, totalMatchesInRound, isLastRound }) => {
  if (isLastRound) return null;
  
  // Each pair of matches connects to one match in the next round
  const isTopOfPair = matchIndex % 2 === 0;
  const connectorHeight = MATCH_HEIGHT + 12; // Match height + gap
  
  return (
    <View style={styles.bracketConnectorContainer}>
      {/* Horizontal line from match to vertical connector */}
      <View style={styles.bracketConnectorHorizontal} />
      {/* Vertical line (only draw full connector for top match of pair) */}
      {isTopOfPair ? (
        <View style={[styles.bracketConnectorVertical, { height: connectorHeight }]} />
      ) : (
        <View style={[styles.bracketConnectorVertical, { height: connectorHeight, top: -connectorHeight }]} />
      )}
      {/* Horizontal line to next round */}
      <View style={[styles.bracketConnectorHorizontalOut, { top: isTopOfPair ? connectorHeight / 2 : -connectorHeight / 2 }]} />
    </View>
  );
};

const BracketModal = ({ visible, onClose, bracket, currentMatch, eventTitle }) => {
  const [selectedMatch, setSelectedMatch] = useState(null);

  if (!visible) return null;

  const handleMatchPress = (match) => {
    setSelectedMatch(match);
  };

  const closeMatchDetail = () => {
    setSelectedMatch(null);
  };

  const rounds = bracket?.rounds || [];
  const totalRounds = rounds.length;

  // Calculate vertical spacing multiplier for each round
  // Round 1: normal spacing, Round 2: 2x spacing, Round 3: 4x spacing, etc.
  const getVerticalSpacing = (roundIndex) => {
    return Math.pow(2, roundIndex);
  };

  // Calculate top offset for matches in each round to center them
  const getTopOffset = (roundIndex) => {
    if (roundIndex === 0) return 0;
    const spacing = getVerticalSpacing(roundIndex);
    const matchBlockHeight = MATCH_HEIGHT + 12; // match + gap
    return (matchBlockHeight * (spacing - 1)) / 2;
  };

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.bracketModalContainer}>
          {/* Header */}
          <View style={styles.bracketModalHeader}>
            <View>
              <Text style={styles.bracketModalTitle}>Tournament Bracket</Text>
              <Text style={styles.bracketModalSubtitle}>{eventTitle}</Text>
            </View>
            <Pressable style={styles.modalCloseBtn} onPress={onClose}>
              <Text style={styles.modalCloseBtnText}>X</Text>
            </Pressable>
          </View>

          {/* Bracket Content */}
          <ScrollView 
            style={styles.bracketScrollV}
            contentContainerStyle={styles.bracketScrollVContent}
          >
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.bracketScrollH}
            >
              <View style={styles.bracketGridNew}>
                {rounds.map((round, roundIndex) => {
                  const isLastRound = roundIndex === totalRounds - 1;
                  const spacingMultiplier = getVerticalSpacing(roundIndex);
                  const topOffset = getTopOffset(roundIndex);
                  const matchGap = (MATCH_HEIGHT + 12) * spacingMultiplier - MATCH_HEIGHT;

                  return (
                    <View key={round.name || roundIndex} style={styles.bracketRoundWrapper}>
                      {/* Round Title */}
                      <Text style={styles.bracketRoundLabel}>{round.name}</Text>
                      
                      {/* Matches Column */}
                      <View style={[styles.bracketRoundMatches, { marginTop: topOffset }]}>
                        {round.matches.map((match, matchIndex) => (
                          <View 
                            key={match.id || matchIndex} 
                            style={[
                              styles.bracketMatchWrapper,
                              { marginBottom: matchIndex < round.matches.length - 1 ? matchGap : 0 }
                            ]}
                          >
                            <BracketMatch
                              match={match}
                              onPress={handleMatchPress}
                              isLive={match.status === 'in_progress'}
                            />
                          </View>
                        ))}
                      </View>

                      {/* Connector Lines (between this round and next) */}
                      {!isLastRound && (
                        <View style={[styles.bracketConnectorsColumn, { marginTop: topOffset }]}>
                          {round.matches.map((match, matchIndex) => {
                            const isTopOfPair = matchIndex % 2 === 0;
                            const pairGap = matchGap;
                            
                            return (
                              <View 
                                key={`conn-${match.id || matchIndex}`}
                                style={[
                                  styles.bracketConnectorWrapper,
                                  { marginBottom: matchIndex < round.matches.length - 1 ? pairGap : 0 }
                                ]}
                              >
                                {/* Horizontal line out of match */}
                                <View style={styles.connectorLineH} />
                                
                                {/* Vertical connector for pairs */}
                                {isTopOfPair && matchIndex + 1 < round.matches.length && (
                                  <View 
                                    style={[
                                      styles.connectorLineV,
                                      { height: MATCH_HEIGHT + pairGap }
                                    ]} 
                                  />
                                )}
                                
                                {/* Horizontal line into next match (only for top of pair) */}
                                {isTopOfPair && (
                                  <View 
                                    style={[
                                      styles.connectorLineHOut,
                                      { top: (MATCH_HEIGHT + pairGap) / 2 }
                                    ]} 
                                  />
                                )}
                              </View>
                            );
                          })}
                        </View>
                      )}
                    </View>
                  );
                })}
                
                {/* Champion placeholder */}
                {rounds.length > 0 && (
                  <View style={styles.bracketChampionWrapper}>
                    <Text style={styles.bracketRoundLabel}>Champion</Text>
                    <View style={[styles.bracketChampionBox, { marginTop: getTopOffset(totalRounds) }]}>
                      {(() => {
                        const finalRound = rounds[rounds.length - 1];
                        const finalMatch = finalRound?.matches?.[0];
                        const winner = finalMatch?.player1?.winner 
                          ? finalMatch?.player1?.name 
                          : finalMatch?.player2?.winner 
                            ? finalMatch?.player2?.name 
                            : null;
                        return (
                          <>
                            <Text style={styles.bracketChampionLabel}>
                              {winner ? 'WINNER' : 'TBD'}
                            </Text>
                            <Text style={styles.bracketChampionName}>
                              {winner || '?'}
                            </Text>
                          </>
                        );
                      })()}
                    </View>
                  </View>
                )}
              </View>
            </ScrollView>
          </ScrollView>

          {/* Match Detail Overlay */}
          {selectedMatch && (
            <View style={styles.matchDetailOverlay}>
              <View style={styles.matchDetailCard}>
                <Pressable style={styles.matchDetailClose} onPress={closeMatchDetail}>
                  <Text style={styles.modalCloseBtnText}>X</Text>
                </Pressable>
                
                <Text style={styles.matchDetailTitle}>
                  {selectedMatch.status === 'in_progress' ? 'LIVE MATCH' : 
                   selectedMatch.status === 'completed' ? 'COMPLETED' : 'UPCOMING'}
                </Text>

                <View style={styles.matchDetailPlayers}>
                  <View style={styles.matchDetailPlayer}>
                    <View style={[styles.matchDetailAvatar, selectedMatch.player1?.winner && styles.matchDetailAvatarWinner]} />
                    <Text style={[styles.matchDetailName, selectedMatch.player1?.winner && styles.matchDetailNameWinner]}>
                      {selectedMatch.player1?.name || 'TBD'}
                    </Text>
                    <Text style={[styles.matchDetailScore, selectedMatch.player1?.winner && styles.matchDetailScoreWinner]}>
                      {selectedMatch.player1?.score ?? 0}
                    </Text>
                  </View>
                  
                  <Text style={styles.matchDetailVs}>VS</Text>
                  
                  <View style={styles.matchDetailPlayer}>
                    <View style={[styles.matchDetailAvatar, selectedMatch.player2?.winner && styles.matchDetailAvatarWinner]} />
                    <Text style={[styles.matchDetailName, selectedMatch.player2?.winner && styles.matchDetailNameWinner]}>
                      {selectedMatch.player2?.name || 'TBD'}
                    </Text>
                    <Text style={[styles.matchDetailScore, selectedMatch.player2?.winner && styles.matchDetailScoreWinner]}>
                      {selectedMatch.player2?.score ?? 0}
                    </Text>
                  </View>
                </View>

                {selectedMatch.status === 'in_progress' && (
                  <Pressable style={styles.watchMatchBtn}>
                    <Text style={styles.watchMatchBtnText}>Watch This Match</Text>
                  </Pressable>
                )}
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

// ---------- Stream Thumbnail ----------
const StreamThumb = ({ participant, isActive, onPress }) => {
  const hasAvatar = participant.avatarUrl || participant.photoURL || participant.profilePic || participant.profilePhotoUrl;
  const avatarUrl = participant.avatarUrl || participant.photoURL || participant.profilePic || participant.profilePhotoUrl;
  const displayName = participant.displayName || participant.ign || participant.odUserId?.slice(0, 8) || 'Player';
  const initials = displayName.slice(0, 2).toUpperCase();
  const isLive = participant.stream && (
    participant.stream.playbackUrl || participant.stream.videoId || participant.stream.channelName
  );
  
  return (
    <Pressable
      style={[styles.streamThumb, isActive && styles.streamThumbActive]}
      onPress={onPress}
    >
      {/* Avatar */}
      <View style={styles.streamThumbAvatar}>
        {hasAvatar ? (
          <Image 
            source={{ uri: avatarUrl }} 
            style={styles.streamThumbAvatarImg}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.streamThumbAvatarPlaceholder}>
            <Text style={styles.streamThumbInitials}>{initials}</Text>
          </View>
        )}
        {/* Live indicator dot */}
        {isLive && <View style={styles.streamThumbLiveDot} />}
      </View>
      
      {/* Player name */}
      <Text style={[styles.streamThumbName, isActive && styles.streamThumbNameActive]} numberOfLines={1}>
        {displayName}
      </Text>
    </Pressable>
  );
};

// ---------- SVG Icons as React Components ----------
// Rumble brand mark — the King Crit die (replaces the old wave glyph).
const RUMBLE_DIE_ICON = require('../../assets/20_dice.webp');
// Purple die for the chat send button.
const PURPLE_DIE_ICON = require('../../assets/20_pdice.webp');
const WaveIcon = ({ size = 18 }) => (
  <Image source={RUMBLE_DIE_ICON} style={{ width: size, height: size }} resizeMode="contain" />
);

const ChatBubbleIcon = ({ size = 18, color = '#fff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M21 11.5C21 16.19 16.97 20 12 20C10.82 20 9.69 19.82 8.65 19.47L3 21L4.3 16.68C3.47 15.25 3 13.53 3 11.5C3 6.81 7.03 3 12 3C16.97 3 21 6.81 21 11.5Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="8" cy="11.5" r="1" fill={color}/>
        <circle cx="12" cy="11.5" r="1" fill={color}/>
        <circle cx="16" cy="11.5" r="1" fill={color}/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 4, fontWeight: '700' }}>...</Text>
    )}
  </View>
);

const FullscreenIcon = ({ size = 18, color = '#fff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M4 8V4H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 8V4H16" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 16V20H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 16V20H16" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 4, fontWeight: '700' }}>[ ]</Text>
    )}
  </View>
);

const ViewerIcon = ({ size = 14, color = '#e11900' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="2"/>
        <path d="M2 12C4 7 7.5 4 12 4C16.5 4 20 7 22 12C20 17 16.5 20 12 20C7.5 20 4 17 2 12Z" stroke={color} strokeWidth="2"/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 2, fontWeight: '700' }}>o</Text>
    )}
  </View>
);

const VolumeIcon = ({ size = 18, color = '#fff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M11 5L6 9H2V15H6L11 19V5Z" fill={color}/>
        <path d="M15.54 8.46C16.48 9.4 17 10.67 17 12C17 13.33 16.48 14.6 15.54 15.54" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <path d="M19.07 4.93C20.94 6.8 22 9.34 22 12C22 14.66 20.94 17.2 19.07 19.07" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 4, fontWeight: '700' }}>Vol</Text>
    )}
  </View>
);

const MutedIcon = ({ size = 18, color = '#fff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M11 5L6 9H2V15H6L11 19V5Z" fill={color}/>
        <path d="M23 9L17 15" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <path d="M17 9L23 15" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 4, fontWeight: '700' }}>Mute</Text>
    )}
  </View>
);

const PlayIcon = ({ size = 18, color = '#fff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M5 4L19 12L5 20V4Z" fill={color}/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 4, fontWeight: '700' }}>Play</Text>
    )}
  </View>
);

const PauseIcon = ({ size = 18, color = '#fff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="6" y="4" width="4" height="16" fill={color}/>
        <rect x="14" y="4" width="4" height="16" fill={color}/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size - 4, fontWeight: '700' }}>||</Text>
    )}
  </View>
);

// ---------- Compact Info Bar ----------
// critsEnabled (evergreen 2.3) is passed from the screen's single listener and
// defaults TRUE so an unpassed prop can never hide the PRIZE item in ON mode.
const CompactInfoBar = ({ event, onExpand, onBracket, onChat, onFlood, onFullscreen, onMatchSelect, chatOpen, unreadCount, isFullscreen, critsEnabled = true, rumbleOpen = false, rumbleCount = 0 }) => {
  // The prize pool is what performers compete for (Crits). Rumbles are a
  // separate real-money stream (a share goes to the players who played once
  // results are final), NOT added to this event's prize pool — never sum them.
  // Rumbles surface separately: the Rumble button (shown only while Rumbles
  // are open for this viewer) with a money-free count badge, and the meter.
  const basePrize = event?.potCents || event?.live?.netPoolCents || 0;
  const totalPrize = basePrize;

  return (
    <View style={styles.compactBar}>
      {/* Left side - Prize and Round info */}
      <View style={styles.compactLeft}>
        {critsEnabled ? (
        <Pressable style={styles.compactItem} onPress={onExpand}>
          <Text style={styles.compactLabel}>PRIZE</Text>
          <Text style={styles.compactValue}>{formatEventPrize(event, totalPrize)}</Text>
        </Pressable>
        ) : (
        /* OFF: money-free stand-in so the expanded sidebar (flood meter,
           current match, bracket, quick stats) stays reachable. */
        <Pressable style={styles.compactItem} onPress={onExpand}>
          <Text style={styles.compactLabel}>EVENT</Text>
          <Text style={styles.compactValue}>Info</Text>
        </Pressable>
        )}

        <Pressable style={styles.compactItem} onPress={onBracket}>
          <Text style={styles.compactLabel}>ROUND</Text>
          <Text style={styles.compactValue}>{event?.currentRound || '-'}/{event?.totalRounds || '-'}</Text>
        </Pressable>
      </View>

      {/* Right side - Action buttons */}
      <View style={styles.compactRight}>
        {/* Match Selector Button */}
        <Pressable style={styles.compactMatchBtn} onPress={onMatchSelect}>
          <Text style={styles.compactMatchIcon}>#</Text>
          <Text style={styles.compactMatchLabel}>Matches</Text>
        </Pressable>
        
        {/* Rumble Button — opens the meter + picker; only while Rumbles are
            open for this viewer + event (setup tipsEnabled) */}
        {FLOOD_ENABLED && rumbleOpen && (
          <Pressable style={styles.compactFloodBtn} onPress={onFlood}>
            <WaveIcon size={16} color="#3b82f6" />
            <Text style={styles.compactFloodLabel}>Rumble</Text>
            {rumbleCount > 0 && (
              <Text style={styles.compactFloodAmount}>{formatRumbleCount(rumbleCount)}</Text>
            )}
          </Pressable>
        )}

        {/* Chat Toggle Button - Chat bubble SVG */}
        <Pressable style={[styles.compactChatBtn, chatOpen && styles.compactChatBtnActive]} onPress={onChat}>
          <ChatBubbleIcon size={18} color={chatOpen ? '#fff' : '#fff'} />
          <Text style={styles.compactChatLabel}>{chatOpen ? 'Close' : 'Chat'}</Text>
          {!chatOpen && unreadCount > 0 && (
            <View style={styles.compactChatBadge}>
              <Text style={styles.compactChatBadgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
            </View>
          )}
        </Pressable>
        
        {/* Fullscreen Button - Fullscreen SVG */}
        <Pressable
          style={[styles.compactFullscreenBtn, isFullscreen && styles.compactFullscreenBtnActive]}
          onPress={onFullscreen}
          accessibilityLabel={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          <FullscreenIcon size={18} color={isFullscreen ? '#FCD000' : '#fff'} />
        </Pressable>
      </View>
    </View>
  );
};

// ---------- Video Player Overlay Controls (simplified - no duplicate fullscreen) ----------
const VideoPlayerOverlay = ({
  visible,
  isPaused,
  isMuted,
  onPlayPause,
  onMuteToggle,
  selectedPlayer,
  opponent,
  onSwitchPlayer,
  onOpenMatchSelector,
  currentMatch,
  onReport,
  participants = [],
}) => {
  // Full-grid FFA (GT7 showdowns): "More v" opens the racer POV picker instead
  // of the match selector — one lobby, many drivers (founder UX 2026-08-06:
  // controls show WHO YOU'RE ON; every other racer lives behind More).
  const [povMenuOpen, setPovMenuOpen] = useState(false);
  const isFFA = participants.length > 2;
  if (!visible) return null;

  return (
    <View style={styles.playerOverlay}>
      {/* Top gradient - match info */}
      <View style={styles.playerOverlayTop}>
        {currentMatch && (
          <Text style={styles.matchLabel}>
            Match {currentMatch.matchNumber || '-'} of {currentMatch.totalMatches || '-'}
          </Text>
        )}
      </View>
      
      {/* Center play button (large) - only show when paused */}
      {isPaused && (
        <Pressable style={styles.centerPlayBtn} onPress={onPlayPause}>
          <PlayIcon size={30} color="#fff" />
        </Pressable>
      )}
      
      {/* Bottom controls */}
      <View style={styles.playerOverlayBottom}>
        {/* Left controls - Play/Pause & Volume */}
        <View style={styles.playerControlsLeft}>
          <Pressable style={styles.playerControlBtn} onPress={onPlayPause}>
            {isPaused ? <PlayIcon size={16} color="#fff" /> : <PauseIcon size={16} color="#fff" />}
          </Pressable>
          
          <Pressable style={styles.playerControlBtn} onPress={onMuteToggle}>
            {isMuted ? <MutedIcon size={16} color="#fff" /> : <VolumeIcon size={16} color="#fff" />}
          </Pressable>
          
          <View style={styles.liveIndicatorSmall}>
            <View style={styles.liveDotSmall} />
            <Text style={styles.liveTextSmall}>LIVE</Text>
          </View>
        </View>
        
        {/* Center - Channel Switcher */}
        <View style={styles.channelSwitcher}>
          {selectedPlayer && (
            <Pressable style={[styles.channelBtn, styles.channelBtnActive]}>
              <View style={styles.channelLiveDot} />
              <Text style={[styles.channelBtnText, styles.channelBtnTextActive]} numberOfLines={1}>
                {selectedPlayer.displayName || 'Player 1'}
              </Text>
            </Pressable>
          )}
          
          {!isFFA && opponent && (
            <Pressable
              style={styles.channelBtn}
              onPress={() => onSwitchPlayer(opponent)}
            >
              {opponent.stream ? (
                <View style={styles.channelLiveDot} />
              ) : (
                <View style={styles.channelOfflineDot} />
              )}
              <Text style={styles.channelBtnText} numberOfLines={1}>
                {opponent.displayName || 'Player 2'}
              </Text>
            </Pressable>
          )}

          {/* More: FFA -> racer POV picker; H2H/brackets -> match selector */}
          <Pressable
            style={styles.moreMatchesBtn}
            onPress={() => (isFFA ? setPovMenuOpen((o) => !o) : onOpenMatchSelector())}
          >
            <Text style={styles.moreMatchesBtnText}>More</Text>
            <Text style={styles.moreMatchesArrow}>{isFFA && povMenuOpen ? '^' : 'v'}</Text>
          </Pressable>

          {isFFA && povMenuOpen && (
            <View style={styles.povMenu}>
              <ScrollView style={{ maxHeight: 300 }}>
                {participants.map((p) => {
                  const active = selectedPlayer?.odUserId === p.odUserId;
                  return (
                    <Pressable
                      key={p.odUserId}
                      style={[styles.povMenuItem, active && styles.povMenuItemActive]}
                      onPress={() => { onSwitchPlayer(p); setPovMenuOpen(false); }}
                    >
                      {p.stream ? (
                        <View style={styles.channelLiveDot} />
                      ) : (
                        <View style={styles.channelOfflineDot} />
                      )}
                      <Text
                        style={[styles.channelBtnText, active && styles.channelBtnTextActive]}
                        numberOfLines={1}
                      >
                        {p.displayName || 'Player'}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
          )}
        </View>
        
        {/* Right side - report the feed being watched (fullscreen is in bottom bar) */}
        <View style={styles.playerControlsRight}>
          {onReport ? (
            <Pressable style={styles.playerControlBtn} onPress={onReport} accessibilityLabel="Report this feed">
              <Text style={styles.reportFlagGlyph}>⚑</Text>
            </Pressable>
          ) : null}
        </View>
      </View>
    </View>
  );
};

// ---------- Match Selector Modal ----------
const MatchSelectorModal = ({ 
  visible, 
  onClose, 
  matches, 
  currentMatchId, 
  onSelectMatch,
  roundNumber,
}) => {
  if (!visible) return null;
  
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.matchSelectorOverlay} onPress={onClose}>
        <View style={styles.matchSelectorContainer}>
          <View style={styles.matchSelectorHeader}>
            <Text style={styles.matchSelectorTitle}>Round {roundNumber} Matches</Text>
            <Pressable style={styles.matchSelectorClose} onPress={onClose}>
              <Text style={styles.matchSelectorCloseText}>X</Text>
            </Pressable>
          </View>
          
          <ScrollView style={styles.matchSelectorList}>
            {matches.map((match, index) => (
              <Pressable
                key={match.id || index}
                style={[
                  styles.matchSelectorItem,
                  currentMatchId === match.id && styles.matchSelectorItemActive
                ]}
                onPress={() => {
                  onSelectMatch(match);
                  onClose();
                }}
              >
                <View style={styles.matchSelectorItemLeft}>
                  <Text style={styles.matchNumber}>#{index + 1}</Text>
                  <View style={styles.matchPlayers}>
                    <View style={styles.matchPlayerRow}>
                      {match.p1?.isLive && <View style={styles.matchLiveDot} />}
                      <Text style={styles.matchPlayerName} numberOfLines={1}>
                        {match.p1?.displayName || match.p1?.ign || 'TBD'}
                      </Text>
                    </View>
                    <Text style={styles.matchVs}>vs</Text>
                    <View style={styles.matchPlayerRow}>
                      {match.p2?.isLive && <View style={styles.matchLiveDot} />}
                      <Text style={styles.matchPlayerName} numberOfLines={1}>
                        {match.p2?.displayName || match.p2?.ign || 'TBD'}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.matchSelectorItemRight}>
                  {(match.p1?.isLive || match.p2?.isLive) ? (
                    <View style={styles.matchLiveBadge}>
                      <Text style={styles.matchLiveText}>LIVE</Text>
                    </View>
                  ) : (
                    <Text style={styles.matchWaitingText}>Waiting</Text>
                  )}
                </View>
              </Pressable>
            ))}
            
            {matches.length === 0 && (
              <View style={styles.matchSelectorEmpty}>
                <Text style={styles.matchSelectorEmptyText}>No matches in this round yet</Text>
              </View>
            )}
          </ScrollView>
        </View>
      </Pressable>
    </Modal>
  );
};

// ---------- Rumble Modal ----------
// `rumble` is the screen-level useRumbleSetup() result, so this modal, the
// compact-bar button and the sidebar meter agree on whether Rumbles are open.
const FloodModal = ({ visible, onClose, eventId, eventTitle, collection, onRequireAuth, onVerify, rumble }) => {
  if (!visible) return null;
  
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.floodModalOverlay}>
        <View style={styles.floodModalContainer}>
          {/* Header */}
          <View style={styles.floodModalHeader}>
            <View style={styles.floodModalTitleRow}>
              <WaveIcon size={20} color="#3b82f6" />
              <Text style={styles.floodModalTitle}>Rumble</Text>
            </View>
            <Pressable style={styles.floodModalClose} onPress={onClose}>
              <Text style={styles.floodModalCloseText}>X</Text>
            </Pressable>
          </View>
          
          {/* Event Info */}
          <View style={styles.floodModalEventInfo}>
            <Text style={styles.floodModalEventTitle} numberOfLines={1}>
              {eventTitle || 'This Match'}
            </Text>
            <Text style={styles.floodModalEventSubtitle}>
              Every Rumble fills the crowd meter.
            </Text>
          </View>

          {/* Tsunami Meter — no inline callbacks: the meter reads its
              callbacks through refs, and the old inline onContribute remounted
              the Stripe form on every ceremony heartbeat. */}
          <ScrollView style={styles.floodModalContent} showsVerticalScrollIndicator={false}>
            <TsunamiMeter
              eventId={eventId}
              collection={collection}
              compact={false}
              showContributions={true}
              onRequireAuth={onRequireAuth}
              onVerify={onVerify}
              rumble={rumble}
            />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

// Current viewport width.
//
// useWindowDimensions alone was NOT sufficient here: on this RNW version it
// reports the right width at mount but was observed not to re-fire on resize,
// which is exactly the case this screen's layout depends on. Seed from the hook
// (correct on native and at first paint) and keep it current on web with a
// direct resize/orientation listener.
function useViewportWidth() {
  const { width } = useWindowDimensions();
  const [w, setW] = useState(width);

  // Adopt RNW's value whenever it does update.
  useEffect(() => {
    setW((prev) => (prev === width ? prev : width));
  }, [width]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const read = () =>
      setW((prev) => (prev === window.innerWidth ? prev : window.innerWidth));
    read();
    window.addEventListener('resize', read);
    window.addEventListener('orientationchange', read);
    return () => {
      window.removeEventListener('resize', read);
      window.removeEventListener('orientationchange', read);
    };
  }, []);

  return w;
}

// ---------- Main Component ----------
export default function EventWatchScreen({ route, navigation }) {
  const { eventId } = route.params || {};
  const isDemo = isDemoEvent(eventId);
  // Evergreen step 2.3: settings/economy.critsEnabled (world-readable, so it
  // works for logged-out spectators). OFF hides the Crit-pool displays only;
  // the flood meter / viewer tips are a separate stream and stay.
  const critsEnabled = useCritsEnabled();

  // Mirrors `participants` so the re-resolve poll can read the current roster
  // without depending on it (which would tear down and rebuild the interval).
  const participantsRef = useRef([]);
  // Read by the stream poll to hunt harder while the ceremony is waiting to
  // hand off. A ref, not state — the poll must not be rebuilt when it flips.
  const ceremonyHandoffRef = useRef(false);

  // Live viewport measurement — re-renders on resize, rotation and fullscreen.
  const winWidth = useViewportWidth();
  const isWide = winWidth > WIDE_BREAKPOINT;
  // A fixed-width chat column squeezed the video to a sliver on small viewports
  // (300px of chat on a 375px phone left ~75px of video). Clamp so the video
  // column always keeps at least MIN_VIDEO_WIDTH.
  const chatWidth = Math.max(
    0,
    Math.min(isWide ? CHAT_WIDTH_WIDE : CHAT_WIDTH_NARROW, winWidth - MIN_VIDEO_WIDTH),
  );

  // Logged-out spectators can watch, but chat/flood funnel them to sign up.
  const [isLoggedIn, setIsLoggedIn] = useState(!!auth.currentUser);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setIsLoggedIn(!!u));
    return unsub;
  }, []);

  // Live viewer count via lightweight Firestore presence heartbeats. Counts everyone
  // on the watch page (including logged-out spectators); stale sessions age out.
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  // STABLE per-tab session id (persisted in sessionStorage) so a page RELOAD
  // reuses the same presence doc instead of spawning a second one that lingers
  // until it ages out — that was why a single viewer could read as 2. Each
  // browser tab still gets its own id, so distinct tabs = distinct real viewers.
  const viewerSessionId = useRef(
    (() => {
      const gen = () => `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          let id = window.sessionStorage.getItem('cc_viewer_sid');
          if (!id) { id = gen(); window.sessionStorage.setItem('cc_viewer_sid', id); }
          return id;
        }
      } catch (e) { /* sessionStorage blocked — fall through */ }
      return gen();
    })()
  ).current;
  useEffect(() => {
    if (!eventId || isDemo) return;
    const sessionsRef = collection(db, 'eventViewers', eventId, 'sessions');
    const presenceRef = doc(sessionsRef, viewerSessionId);
    const HEARTBEAT_MS = 20000;
    // Generous stale window so a backgrounded tab (whose timers the browser
    // throttles) doesn't flicker the count down and back up on tab switches.
    const STALE_MS = 90000;

    // Logged-in viewers stamp their uid (rules enforce it matches auth) — the
    // audienceDraw comp signal counts SIGNED-IN viewers only. Anonymous
    // sessions heartbeat updatedAt-only: visible in the live count, no comp.
    //
    // Alongside the ephemeral presence doc, every beat also touches the
    // PERSISTENT audience roster (eventAudience/{eventId}/viewers/{uid} or
    // /anon/{sessionId}) — that's what comp + metrics read at completion, since
    // presence docs self-delete when viewers leave. Watch time accrues +20s per
    // beat, but only on beats AFTER the roster doc was touched once this mount
    // (the arrival beat adds 0 — you haven't watched an interval yet), and
    // firestore.rules meter the increment (≥15s gap, ≤+25s) so it can't accrue
    // faster than wall-clock. Denied/too-soon beats are silently dropped.
    const rosterTouched = { current: false };
    const rosterBeat = () => {
      const uid = auth.currentUser?.uid;
      const rosterRef = uid
        ? doc(db, 'eventAudience', eventId, 'viewers', uid)
        : doc(db, 'eventAudience', eventId, 'anon', viewerSessionId);
      const delta = rosterTouched.current ? 20 : 0;
      updateDoc(rosterRef, {
        lastSeenAt: serverTimestamp(),
        watchSeconds: increment(delta),
      }).then(() => { rosterTouched.current = true; }).catch(() => {
        // Doc may not exist yet — create it (rules require exactly this shape).
        // A too-soon beat (<15s since last) also lands here; the create attempt
        // is then denied by rules (existing doc → firstSeenAt is immutable),
        // which safely drops the beat.
        setDoc(rosterRef, {
          firstSeenAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),
          watchSeconds: 0,
        }).then(() => { rosterTouched.current = true; }).catch(() => {});
      });
    };
    const beat = () => {
      const uid = auth.currentUser?.uid;
      const payload = uid
        ? { updatedAt: serverTimestamp(), userId: uid }
        : { updatedAt: serverTimestamp() };
      setDoc(presenceRef, payload, { merge: true }).catch(() => {});
      rosterBeat();
    };
    beat();
    const interval = setInterval(beat, HEARTBEAT_MS);

    // Re-beat the moment the tab regains focus (background tabs throttle timers).
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') beat();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);

    const unsub = onSnapshot(sessionsRef, (snap) => {
      const cutoff = Date.now() - STALE_MS;
      let count = 0;
      snap.forEach((d) => {
        const ms = d.data()?.updatedAt?.toMillis?.() || 0;
        if (ms >= cutoff) count += 1;
      });
      setLiveViewerCount(count);
    }, () => {});

    const cleanup = () => { deleteDoc(presenceRef).catch(() => {}); };
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('beforeunload', cleanup);
    }
    return () => {
      clearInterval(interval);
      unsub();
      cleanup();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', cleanup);
      }
    };
  }, [eventId, isDemo]);

  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  // The event-doc snapshot handlers also write participants, so mirror every
  // change — not just the ones the poll makes.
  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [floodContributions, setFloodContributions] = useState([]);
  const [selectedStream, setSelectedStream] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // UI State
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [bracketVisible, setBracketVisible] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [floodModalVisible, setFloodModalVisible] = useState(false);
  // Screen-level rumble burst: the milestone watcher must outlive the Rumble
  // modal / sidebar (the only TsunamiMeter mounts, both closed by default) or
  // no one is watching the meter total when a crossing lands — which is why
  // the dice never fell over the 08-07 ceremony. Cents milestone or 0.
  const [rumbleBurst, setRumbleBurst] = useState(0);
  const prevFloodRef = useRef({ eventId: null, total: null });
  const [bracket, setBracket] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Video player controls state
  const [showControls, setShowControls] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  // The element LiveHlsPlayer is currently showing. The double buffer swaps
  // elements on POV switches, so feed listeners key on this, not videoRef
  // (which the player keeps pointed at the same element for the overlay).
  const [activeVideoEl, setActiveVideoEl] = useState(null);

  // Moderation + reporting (parity with StreamWatch — founder 2026-07-10):
  // tap a chat handle → user card (profile / hide / report); overlay flag
  // reports the selected participant's feed. Event chat has no single owner,
  // so there's no mute here — admins moderate via rules.
  const [chatUserCard, setChatUserCard] = useState(null); // { user, userId, messageId, messageText } | null
  const [hiddenChatUids, setHiddenChatUids] = useState(() => new Set());
  const [reportTarget, setReportTarget] = useState(null); // { kind, uid, name, messageId, messageText } | null
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportDetails, setReportDetails] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);

  const openProfile = (userName) => {
    if (!userName || userName === 'Player' || userName === 'CritCash streamer') return;
    navigation.navigate('PublicProfile', { userName });
  };

  const toggleHideChatUser = (targetUid) => {
    setHiddenChatUids((prev) => {
      const next = new Set(prev);
      if (next.has(targetUid)) next.delete(targetUid);
      else next.add(targetUid);
      return next;
    });
    setChatUserCard(null);
  };

  const openReport = (kind, target) => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    if (!target?.uid) return;
    setReportReason('inappropriate');
    setReportDetails('');
    setReportStatus(null);
    setReportTarget({ kind, ...target });
    setChatUserCard(null);
  };

  const submitReport = async () => {
    if (reportBusy || !reportTarget) return;
    setReportBusy(true);
    setReportStatus(null);
    try {
      await httpsCallable(getFunctions(), 'reportStreamContent')({
        kind: reportTarget.kind,
        eventId,
        streamerUid: reportTarget.kind === 'stream' ? reportTarget.uid : undefined,
        targetUid: reportTarget.uid,
        messageId: reportTarget.messageId || null,
        messageText: reportTarget.messageText || null,
        reason: reportReason,
        details: reportDetails,
      });
      setReportStatus('sent');
      setTimeout(() => setReportTarget(null), 1600);
    } catch (err) {
      const raw = String(err?.message || '');
      setReportStatus(raw.includes('already-reported')
        ? "You've already reported this — our team is on it."
        : 'Could not send the report. Please try again.');
    } finally {
      setReportBusy(false);
    }
  };
  const controlsTimeoutRef = useRef(null);
  const videoRef = useRef(null);
  
  // Match selection state
  const [roundMatches, setRoundMatches] = useState([]); // All matches in current round
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchSelectorVisible, setMatchSelectorVisible] = useState(false);
  
  // Get opponent in current match
  const opponent = useMemo(() => {
    if (!participants || participants.length < 2) return null;
    return participants.find(p => p.odUserId !== selectedStream?.odUserId);
  }, [participants, selectedStream]);

  // Handle showing/hiding controls on hover
  const handleMouseEnter = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  }, []);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  // Fullscreen toggle handler
  const toggleFullscreen = useCallback(() => {
    if (Platform.OS !== 'web') return;
    // Fullscreen the whole video COLUMN, not just #video-area. floatingTopBar
    // (back, event title, viewer count) is a sibling of #video-area, so
    // fullscreening that alone dropped it off the screen and left the player
    // controls looking clipped. The column carries the top bar, the overlay and
    // the compact info bar together; chat is outside it and stays out.
    const target = document.getElementById('video-column') || document.getElementById('video-area');
    if (!target) return;

    const active = document.fullscreenElement || document.webkitFullscreenElement;
    if (!active) {
      // requestFullscreen returns a Promise, so `a?.() || b?.()` never reached the
      // webkit fallback. Pick the function that exists, and swallow the rejection
      // the browser throws when the gesture isn't trusted.
      const request = target.requestFullscreen || target.webkitRequestFullscreen;
      try {
        Promise.resolve(request?.call(target)).catch(() => {});
      } catch (_) { /* fullscreen unavailable — leave the player inline */ }
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      try {
        Promise.resolve(exit?.call(document)).catch(() => {});
      } catch (_) { /* already exited */ }
    }
    // State is NOT set here — the fullscreenchange listener below is the single
    // source of truth, so ESC and the browser's own exit control stay in sync.
  }, []);

  // Track real fullscreen state. Exiting with ESC fires fullscreenchange but no
  // click, so an optimistic setIsFullscreen(true) would stay stuck on.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const sync = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  // Rumble meter = legacy flood fields + 3.3 rumble fields (3.3 only moves the
  // rumble* ones). Cents for the burst ladder; the count is what fans see.
  const rumbleMeter = event ? eventRumbleTotals(event) : null;
  const rumbleMeterTotal = rumbleMeter ? rumbleMeter.totalCents : null;
  const rumbleCount = rumbleMeter ? rumbleMeter.count : 0;

  // Rumble setup for this viewer + event, screen-level so the compact-bar
  // button, the modal and the sidebar meter agree. Event Rumbles target
  // tournaments/{id} only (the server ignores a legacy collection), so a
  // deprecated matches doc or a demo event never asks. Re-fetched whenever the
  // event's status changes: completing or cancelling hides the button.
  const rumbleTarget = useMemo(() => (
    FLOOD_ENABLED && eventId && !isDemo && event?._source === 'tournaments'
      ? { targetType: 'event', targetId: eventId }
      : null
  ), [eventId, isDemo, event?._source]);
  const rumble = useRumbleSetup(rumbleTarget, event?.status || null, {
    loggedOutLive: event?.status === 'live',
  });

  // Sidebar meter gate. Once the meter has mounted for this event it stays
  // mounted (latched per eventId): its embedded checkout and its last outcome
  // live inside it, and a status change (tipsEnabled -> false) or a setup
  // re-read that lands before the webhook bumps the count must not destroy an
  // open Stripe form or a fresh "Rumble sent!".
  const [rumbleShownFor, setRumbleShownFor] = useState(null);
  const rumbleMeterGate = FLOOD_ENABLED && (rumble.open || rumbleCount > 0);
  useEffect(() => {
    if (sidebarExpanded && rumbleMeterGate && eventId && rumbleShownFor !== eventId) {
      setRumbleShownFor(eventId);
    }
  }, [sidebarExpanded, rumbleMeterGate, eventId, rumbleShownFor]);
  const showSidebarRumbleMeter = FLOOD_ENABLED
    && (rumble.open || rumbleCount > 0 || (!!eventId && rumbleShownFor === eventId));

  // Milestone watcher on the screen's own event subscription (alive through
  // every ceremony phase, unlike the modal-gated TsunamiMeter instances). The
  // first observation per event seeds silently — a viewer arriving after a
  // crossing shouldn't get a stale burst, and neither should cross-event
  // navigation (prev.eventId check). claimRumbleMilestone dedupes against an
  // open Rumble modal watching the same crossing.
  useEffect(() => {
    if (rumbleMeterTotal === null) return;
    const total = rumbleMeterTotal;
    const prev = prevFloodRef.current;
    if (prev.eventId !== eventId || prev.total === null) {
      prevFloodRef.current = { eventId, total };
      return;
    }
    if (total !== prev.total) {
      // Decreases go through too: watching the total DROP (admin reset) is
      // what re-arms the per-event burst latch.
      const claimed = claimRumbleMilestone(eventId, prev.total, total);
      if (claimed) setRumbleBurst(claimed);
    }
    prevFloodRef.current = { eventId, total };
  }, [rumbleMeterTotal, eventId]);

  // Mux CDN playback URLs are now fetched inline during participant loading
  // (no delayed post-load enhancement needed)

  // Load event data from Firestore (demo events are seeded by seedDemoData.js)
  useEffect(() => {

    // Helper to get game info
    const getGameInfo = (data) => {
      let gameId = data.game || data.gameId || '';
      
      if (!gameId && data.requiredStreamTitle) {
        const title = data.requiredStreamTitle.toUpperCase();
        if (title.includes('RKL')) gameId = 'rkl';
        else if (title.includes('COD')) gameId = 'cod';
        else if (title.includes('VAL')) gameId = 'val';
        else if (title.includes('SF6')) gameId = 'sf6';
        else if (title.includes('TK8')) gameId = 'tk8';
        else if (title.includes('LOL')) gameId = 'lol';
        else if (title.includes('FC25') || title.includes('FIFA')) gameId = 'fc25';
        else if (title.includes('MK1')) gameId = 'mk1';
        else if (title.includes('SSBU') || title.includes('SMASH')) gameId = 'ssbu';
      }
      
      // Lookup from GAME_PROFILES would be ideal, but for now use hardcoded mappings
      const gameMap = {
        // Fighting
        'sf6': { abbr: 'SF6', displayName: 'Street Fighter 6', type: 'fighting' },
        'tk8': { abbr: 'TK8', displayName: 'Tekken 8', type: 'fighting' },
        'mk1': { abbr: 'MK1', displayName: 'Mortal Kombat 1', type: 'fighting' },
        'ssb': { abbr: 'SSB', displayName: 'Super Smash Bros. Ultimate', type: 'fighting' },
        '2xk': { abbr: '2XK', displayName: '2XKO', type: 'fighting' },

        // Shooters
        'cod': { abbr: 'COD', displayName: 'Call of Duty: Black Ops 7', type: 'shooter' },
        'val': { abbr: 'VAL', displayName: 'Valorant', type: 'shooter' },
        'cs2': { abbr: 'CS2', displayName: 'Counter-Strike 2', type: 'shooter' },
        'rb6': { abbr: 'RB6', displayName: 'Rainbow Six Siege X', type: 'shooter' },
        'ow2': { abbr: 'OW2', displayName: 'Overwatch 2', type: 'shooter' },
        'apl': { abbr: 'APL', displayName: 'Apex Legends', type: 'shooter' },
        'mvr': { abbr: 'MVR', displayName: 'Marvel Rivals', type: 'shooter' },

        // Sports
        'rkl': { abbr: 'RL', displayName: 'Rocket League', type: 'sports' },
        'mdn': { abbr: 'M26', displayName: 'Madden 26', type: 'sports' },
        'n2k': { abbr: '2K26', displayName: 'NBA 2K26', type: 'sports' },

        // Racing
        'irc': { abbr: 'iRACE', displayName: 'iRacing', type: 'racing' },
        'gt7': { abbr: 'GT7', displayName: 'Gran Turismo 7', type: 'racing' },

        // MOBA
        'lol': { abbr: 'LOL', displayName: 'League of Legends', type: 'moba' },
        'dt2': { abbr: 'DOTA', displayName: 'Dota 2', type: 'moba' },
      };
      
      const info = gameMap[gameId] || { abbr: gameId?.toUpperCase() || '???', displayName: gameId || 'Unknown', type: 'other' };
      return { id: gameId, ...info };
    };

    // Helper to map match data to event format
    const mapMatchToEvent = (snap) => {
      const data = snap.data();
      const gameInfo = getGameInfo(data);
      
      // Crit matches carry their pool in whole Crits (1 Crit = $1). Prefer a
      // stored critPotCount; otherwise derive from critEntryCost × entrants.
      const isCritEvent = (data.entryCurrency === 'crits') || (data.critEntryCost != null);
      const critEntryCost = Math.max(0, Math.floor(Number(data.critEntryCost || 0)));
      const burnRate = Math.max(0, Math.min(1, Number(data.rakePercent ?? 10) / 100));
      let potCents, netPrizeCents;
      if (isCritEvent) {
        potCents = (data.critPotCount != null)
          ? Number(data.critPotCount)
          : (data.entrantsCount || 2) * critEntryCost;
        netPrizeCents = Math.ceil(potCents * (1 - burnRate));
      } else {
        potCents = data.potCents || 0;
        const rakeCents = data.rakeCents || 0;
        netPrizeCents = potCents - rakeCents;
      }

      return {
        id: snap.id,
        _source: 'matches',
        title: data.title || data.requiredStreamTitle || 'Live Match',
        status: data.status,
        gameId: gameInfo.id,
        game: gameInfo,
        category: gameInfo.type,
        entrantsCount: data.entrantsCount || 2,
        isCritEvent,
        live: { netPoolCents: netPrizeCents },
        potCents: potCents,
        // Rumble meter (eventRumbleTotals sums legacy flood* + 3.3 rumble*)
        floodTotalCents: data.floodTotalCents || 0,
        floodCount: data.floodCount || 0,
        rumbleTotalCents: data.rumbleTotalCents || 0,
        rumbleCount: data.rumbleCount || 0,
        viewerCount: data.viewerCount || 0,
        startedAt: data.startedAt,
        currentRound: data.currentRound || 1,
        totalRounds: data.totalRounds || 1,
        entries: data.entries,
        requiresStreaming: data.requiresStreaming,
        requiredStreamTitle: data.requiredStreamTitle,
        _rawData: data,
      };
    };

    // Helper to map tournament data to event format
    const mapTournamentToEvent = (snap) => {
      const data = snap.data();
      const gameInfo = getGameInfo(data);
      
      const entrantsCount = data.entrantsCount || 0;
      const entryFeeCents = data.entryFeeCents || 0;
      // Crit events: pool denominated in whole Crits (1 Crit = $1), computed from
      // critEntryCost like Lobby/LiveMatches; net pool rounds UP after the burn.
      const isCritEvent = (data.entryCurrency === 'crits') || (data.critEntryCost != null);
      const critEntryCost = Math.max(0, Math.floor(Number(data.critEntryCost || 0)));
      const burnRate = Math.max(0, Math.min(1, Number(data.rakePercent ?? 10) / 100));
      let totalPot, netPrizeCents;
      if (isCritEvent) {
        totalPot = entrantsCount * critEntryCost;
        netPrizeCents = Math.ceil(totalPot * (1 - burnRate));
      } else {
        totalPot = entrantsCount * entryFeeCents;
        const rakeCents = data.rakeCents || Math.floor(totalPot * 0.1);
        netPrizeCents = totalPot - rakeCents;
      }

      return {
        id: snap.id,
        _source: 'tournaments',
        title: data.title || data.name || `${gameInfo.displayName} Tournament`,
        status: data.status,
        gameId: gameInfo.id,
        game: gameInfo,
        category: gameInfo.type,
        entrantsCount: entrantsCount,
        isCritEvent,
        live: { netPoolCents: netPrizeCents },
        potCents: totalPot,
        // Rumble meter (eventRumbleTotals sums legacy flood* + 3.3 rumble*)
        floodTotalCents: data.floodTotalCents || 0,
        floodCount: data.floodCount || 0,
        rumbleTotalCents: data.rumbleTotalCents || 0,
        rumbleCount: data.rumbleCount || 0,
        viewerCount: data.viewerCount || 0,
        startedAt: data.startedAt,
        currentRound: data.currentRound || 1,
        totalRounds: data.totalRounds || data.actualRounds || (data.bracketSize ? Math.ceil(Math.log2(data.bracketSize)) : 1),
        formatName: data.formatName,
        bracketSize: data.bracketSize,
        entries: data.entries,
        _rawData: data,
      };
    };

    let matchUnsub = null;
    let tournamentUnsub = null;
    let floodUnsub = null;
    let foundInMatches = false;
    let foundInTournaments = false;

    // Try to find in matches collection first
    matchUnsub = onSnapshot(doc(db, 'matches', eventId), async (snap) => {
      if (snap.exists()) {
        foundInMatches = true;
        const mappedEvent = mapMatchToEvent(snap);
        setEvent(mappedEvent);
        
        // Set participants from entries
        const data = snap.data();
        const allParticipants = [];
        
        // Get entry user IDs from both document map and subcollection
        const entryUserIds = [];
        const entryDisplayNames = {}; // userId -> displayName
        const entryAvatars = {}; // userId -> avatarUrl
        
        // Check entries map on document
        if (data.entries) {
          Object.entries(data.entries).forEach(([odUserId, entryData]) => {
            entryUserIds.push(odUserId);
            entryDisplayNames[odUserId] = entryData.displayName || entryData.ign || odUserId;
            entryAvatars[odUserId] = entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null;
            // Include if they have external stream data (Twitch/YouTube)
            if (entryData.stream) {
              allParticipants.push({
                odUserId,
                displayName: entryData.displayName || entryData.ign || odUserId,
                avatarUrl: entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null,
                stream: entryData.stream,
              });
            }
          });
        }
        
        // Also query entries subcollection
        try {
          const entriesSubSnap = await getDocs(
            query(collection(db, 'matches', eventId, 'entries'), limit(entriesReadCap(data)))
          );
          for (const entryDoc of entriesSubSnap.docs) {
            const odUserId = entryDoc.id;
            const entryData = entryDoc.data();
            if (!entryUserIds.includes(odUserId)) {
              entryUserIds.push(odUserId);
            }
            entryDisplayNames[odUserId] = entryData.displayName || entryData.ign || odUserId;
            entryAvatars[odUserId] = entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null;
            // Include if they have external stream data
            if (entryData.stream && !allParticipants.find(p => p.odUserId === odUserId)) {
              allParticipants.push({
                odUserId,
                displayName: entryData.displayName || entryData.ign || odUserId,
                avatarUrl: entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null,
                stream: entryData.stream,
              });
            }
          }
        } catch (err) {
          console.log('Error fetching entries subcollection:', err);
        }
        
        // Also check p1/p2 for H2H matches
        const p1Id = data.p1?.odUserId || data.p1?.odUserID || data.p1?.userId;
        const p2Id = data.p2?.odUserId || data.p2?.odUserID || data.p2?.userId;
        if (p1Id) {
          if (!entryUserIds.includes(p1Id)) entryUserIds.push(p1Id);
          entryDisplayNames[p1Id] = data.p1?.displayName || data.p1?.ign || p1Id;
          entryAvatars[p1Id] = data.p1?.avatarUrl || data.p1?.photoURL || data.p1?.profilePic || null;
          // If p1 has stream data, add to participants
          if (data.p1?.stream && !allParticipants.find(p => p.odUserId === p1Id)) {
            allParticipants.push({
              odUserId: p1Id,
              displayName: data.p1?.displayName || data.p1?.ign || p1Id,
              avatarUrl: data.p1?.avatarUrl || data.p1?.photoURL || data.p1?.profilePic || null,
              stream: data.p1.stream,
            });
          }
        }
        if (p2Id) {
          if (!entryUserIds.includes(p2Id)) entryUserIds.push(p2Id);
          entryDisplayNames[p2Id] = data.p2?.displayName || data.p2?.ign || p2Id;
          entryAvatars[p2Id] = data.p2?.avatarUrl || data.p2?.photoURL || data.p2?.profilePic || null;
          // If p2 has stream data, add to participants
          if (data.p2?.stream && !allParticipants.find(p => p.odUserId === p2Id)) {
            allParticipants.push({
              odUserId: p2Id,
              displayName: data.p2?.displayName || data.p2?.ign || p2Id,
              avatarUrl: data.p2?.avatarUrl || data.p2?.photoURL || data.p2?.profilePic || null,
              stream: data.p2.stream,
            });
          }
        }
        
        // NOTE: there used to be a client-side streamKeys query here. It asked for
        // `status == active` with limit(50) and NO event scoping or ordering, then
        // filtered the result down to this event's entrants — so it fetched 50
        // arbitrary active keys from the whole platform and kept whichever
        // happened to be racing here. Past 50 concurrent streamers, whether a
        // racer appeared came down to where their random key sorted.
        // It was also unreadable for everyone but admins (firestore.rules
        // streamKeys: owner-or-admin), so the error was swallowed and ordinary
        // spectators got nothing from it. getEventStreams below resolves the same
        // thing server-side, event-scoped and with full authority.

        // Public stream discovery (works for logged-out spectators): entrants live in
        // the sign-in-gated entries subcollection, so we can't enumerate them client-
        // side. The public getEventStreams endpoint resolves the live streams server-
        // side and returns only public-safe info (display name + signed playback URL).
        try {
          const fns = getFunctions();
          const getEventStreams = httpsCallable(fns, 'getEventStreams');
          const res = await getEventStreams({ eventId });
          for (const s of (res.data?.streams || [])) {
            if (!s?.odUserId) continue;
            const stream = {
              provider: 'critcash',
              playbackUrl: s.playbackUrl,
              source: s.source,
              thumbnailUrl: s.thumbnailUrl || null,
            };
            const existing = allParticipants.find(p => p.odUserId === s.odUserId);
            if (existing) {
              existing.stream = stream;
            } else {
              allParticipants.push({
                odUserId: s.odUserId,
                displayName: entryDisplayNames[s.odUserId] || s.displayName || s.odUserId,
                avatarUrl: entryAvatars[s.odUserId] || null,
                stream,
              });
            }
          }
        } catch (err) {
          console.log('Error fetching event streams:', err);
        }

        // Fetch Mux CDN playback URLs for CritCash streams
        try {
          const functions = getFunctions();
          const getStreamPlaybackUrl = httpsCallable(functions, 'getStreamPlaybackUrl');

          for (const participant of allParticipants) {
            if (participant.stream?.provider === 'critcash' && !participant.stream?.playbackUrl) {
              try {
                const result = await getStreamPlaybackUrl({
                  odisplayName: participant.odUserId || participant.displayName,
                });
                if (result.data?.available && result.data?.playbackUrl) {
                  participant.stream.playbackUrl = result.data.playbackUrl;
                  participant.stream.source = result.data.source || 'mux';
                  participant.stream.thumbnailUrl = result.data.thumbnailUrl || null;
                  console.log(`[Mux] Got CDN URL for ${participant.displayName}`);
                }
              } catch (muxErr) {
                console.log(`[Mux] Could not get playback URL for ${participant.displayName}:`, muxErr.message);
              }
            }
          }
        } catch (err) {
          console.log('Error fetching Mux playback URLs:', err);
        }

        // Fetch profile photos from public_profiles for all participants
        try {
          const { getDoc } = await import('firebase/firestore');
          const userIds = allParticipants.map(p => p.odUserId).filter(Boolean);

          for (const odUserId of userIds) {
            try {
              const profileRef = doc(db, 'public_profiles', odUserId);
              const profileSnap = await getDoc(profileRef);

              if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                if (profileData.profilePhotoUrl) {
                  // Find and update the participant with profile photo
                  const participant = allParticipants.find(p => p.odUserId === odUserId);
                  if (participant) {
                    participant.avatarUrl = profileData.profilePhotoUrl;
                    participant.profilePhotoUrl = profileData.profilePhotoUrl;
                    console.log(`Match: Loaded profile photo for ${odUserId}`);
                  }
                }
              }
            } catch (profileErr) {
              console.log(`Could not load profile for ${odUserId}:`, profileErr.code);
            }
          }
        } catch (err) {
          console.log('Error loading profile photos:', err);
        }

        // Keep only participants whose stream actually resolves (live). This matches
        // the prior streamKeys-based behavior and drops entrants who aren't streaming.
        const liveParticipants = allParticipants.filter(p => getEmbedUrl(p.stream));
        setParticipants(liveParticipants);
        setSelectedStream((prev) => pickSelectedStream(prev, liveParticipants, allParticipants));
        setLoading(false);
      } else if (!foundInTournaments) {
        // Not in matches, will check tournaments
      }
    }, (error) => {
      console.log('Match not found or error:', error);
    });

    // Also try tournaments collection
    tournamentUnsub = onSnapshot(doc(db, 'tournaments', eventId), async (snap) => {
      if (snap.exists() && !foundInMatches) {
        foundInTournaments = true;
        const mappedEvent = mapTournamentToEvent(snap);
        setEvent(mappedEvent);
        
        // Set participants from entries
        const data = snap.data();
        const allParticipants = [];
        
        // Get entry user IDs from both document map and subcollection
        const entryUserIds = [];
        const entryDisplayNames = {}; // userId -> displayName
        const entryAvatars = {}; // userId -> avatarUrl
        
        // Check entries map on document
        if (data.entries) {
          Object.entries(data.entries).forEach(([odUserId, entryData]) => {
            entryUserIds.push(odUserId);
            entryDisplayNames[odUserId] = entryData.displayName || entryData.ign || odUserId;
            entryAvatars[odUserId] = entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null;
            // Include if they have external stream data (Twitch/YouTube)
            if (entryData.stream) {
              allParticipants.push({
                odUserId,
                displayName: entryData.displayName || entryData.ign || odUserId,
                avatarUrl: entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null,
                stream: entryData.stream,
              });
            }
          });
        }
        
        // Also query entries subcollection
        try {
          const entriesSubSnap = await getDocs(
            query(collection(db, 'tournaments', eventId, 'entries'), limit(entriesReadCap(data)))
          );
          for (const entryDoc of entriesSubSnap.docs) {
            const odUserId = entryDoc.id;
            const entryData = entryDoc.data();
            if (!entryUserIds.includes(odUserId)) {
              entryUserIds.push(odUserId);
            }
            entryDisplayNames[odUserId] = entryData.displayName || entryData.ign || odUserId;
            entryAvatars[odUserId] = entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null;
            // Include if they have external stream data
            if (entryData.stream && !allParticipants.find(p => p.odUserId === odUserId)) {
              allParticipants.push({
                odUserId,
                displayName: entryData.displayName || entryData.ign || odUserId,
                avatarUrl: entryData.avatarUrl || entryData.photoURL || entryData.profilePic || null,
                stream: entryData.stream,
              });
            }
          }
        } catch (err) {
          console.log('Error fetching entries subcollection:', err);
        }
        
        // NOTE: there used to be a client-side streamKeys query here. It asked for
        // `status == active` with limit(50) and NO event scoping or ordering, then
        // filtered the result down to this event's entrants — so it fetched 50
        // arbitrary active keys from the whole platform and kept whichever
        // happened to be racing here. Past 50 concurrent streamers, whether a
        // racer appeared came down to where their random key sorted.
        // It was also unreadable for everyone but admins (firestore.rules
        // streamKeys: owner-or-admin), so the error was swallowed and ordinary
        // spectators got nothing from it. getEventStreams below resolves the same
        // thing server-side, event-scoped and with full authority.

        // Public stream discovery (works for logged-out spectators): entrants live in
        // the sign-in-gated entries subcollection, so we can't enumerate them client-
        // side. The public getEventStreams endpoint resolves the live streams server-
        // side and returns only public-safe info (display name + signed playback URL).
        try {
          const fns = getFunctions();
          const getEventStreams = httpsCallable(fns, 'getEventStreams');
          const res = await getEventStreams({ eventId });
          for (const s of (res.data?.streams || [])) {
            if (!s?.odUserId) continue;
            const stream = {
              provider: 'critcash',
              playbackUrl: s.playbackUrl,
              source: s.source,
              thumbnailUrl: s.thumbnailUrl || null,
            };
            const existing = allParticipants.find(p => p.odUserId === s.odUserId);
            if (existing) {
              existing.stream = stream;
            } else {
              allParticipants.push({
                odUserId: s.odUserId,
                displayName: entryDisplayNames[s.odUserId] || s.displayName || s.odUserId,
                avatarUrl: entryAvatars[s.odUserId] || null,
                stream,
              });
            }
          }
        } catch (err) {
          console.log('Error fetching event streams:', err);
        }

        // Fetch Mux CDN playback URLs for CritCash streams
        try {
          const functions = getFunctions();
          const getStreamPlaybackUrl = httpsCallable(functions, 'getStreamPlaybackUrl');

          for (const participant of allParticipants) {
            if (participant.stream?.provider === 'critcash' && !participant.stream?.playbackUrl) {
              try {
                const result = await getStreamPlaybackUrl({
                  odisplayName: participant.odUserId || participant.displayName,
                });
                if (result.data?.available && result.data?.playbackUrl) {
                  participant.stream.playbackUrl = result.data.playbackUrl;
                  participant.stream.source = result.data.source || 'mux';
                  participant.stream.thumbnailUrl = result.data.thumbnailUrl || null;
                  console.log(`[Mux] Got CDN URL for ${participant.displayName}`);
                }
              } catch (muxErr) {
                console.log(`[Mux] Could not get playback URL for ${participant.displayName}:`, muxErr.message);
              }
            }
          }
        } catch (err) {
          console.log('Error fetching Mux playback URLs:', err);
        }

        // Fetch profile photos from public_profiles for all participants
        try {
          const { getDoc } = await import('firebase/firestore');
          const userIds = allParticipants.map(p => p.odUserId).filter(Boolean);

          for (const odUserId of userIds) {
            try {
              const profileRef = doc(db, 'public_profiles', odUserId);
              const profileSnap = await getDoc(profileRef);

              if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                if (profileData.profilePhotoUrl) {
                  // Find and update the participant with profile photo
                  const participant = allParticipants.find(p => p.odUserId === odUserId);
                  if (participant) {
                    participant.avatarUrl = profileData.profilePhotoUrl;
                    participant.profilePhotoUrl = profileData.profilePhotoUrl;
                    console.log(`Tournament: Loaded profile photo for ${odUserId}`);
                  }
                }
              }
            } catch (profileErr) {
              console.log(`Could not load profile for ${odUserId}:`, profileErr.code);
            }
          }
        } catch (err) {
          console.log('Error loading profile photos:', err);
        }

        // Keep only participants whose stream actually resolves (live). This matches
        // the prior streamKeys-based behavior and drops entrants who aren't streaming.
        const liveParticipants = allParticipants.filter(p => getEmbedUrl(p.stream));
        setParticipants(liveParticipants);
        setSelectedStream((prev) => pickSelectedStream(prev, liveParticipants, allParticipants));
        setLoading(false);
      } else if (!foundInMatches && !snap.exists()) {
        // Not found in either collection
        setEvent(null);
        setLoading(false);
      }
    }, (error) => {
      console.log('Tournament not found or error:', error);
      if (!foundInMatches) {
        setLoading(false);
      }
    });

    // Subscribe to the Rumble ticker feed. Events live under tournaments/ (the
    // top-level matches collection is deprecated and its floodContributions
    // are unreadable), so listen there directly. The old matches-first probe
    // opened this listener from its error callback and never unsubscribed it.
    floodUnsub = onSnapshot(
      query(collection(db, 'tournaments', eventId, 'floodContributions'), orderBy('createdAt', 'desc'), limit(20)),
      (snap) => {
        setFloodContributions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      () => {} // Ignore errors (the feed may not exist yet)
    );

    return () => {
      if (matchUnsub) matchUnsub();
      if (tournamentUnsub) tournamentUnsub();
      if (floodUnsub) floodUnsub();
    };
  }, [eventId]);

  // ============================================================================
  // LIVE FEED RE-RESOLVE
  // ============================================================================
  // Participant resolution above hangs off the event-document snapshot, but a
  // racer going live NEVER writes to the event doc — it writes liveStreams /
  // muxStreams / the relay's stream doc. So a spectator who loads the page
  // before the feeds come up resolves an empty roster and sits on "Select a
  // stream to watch" until they manually refresh. That was the demo-shoot bug.
  //
  // getEventStreams is the public, server-authoritative resolver (it works for
  // logged-out spectators, who can't read the entries subcollection or
  // streamKeys at all). Poll it and merge the result in. This also refreshes
  // Mux's signed playback tokens, which expire after 2h — longer events had no
  // path to a fresh token before.
  useEffect(() => {
    if (!eventId || isDemo) return;

    let cancelled = false;
    let timer = null;
    const fns = getFunctions();
    const getEventStreams = httpsCallable(fns, 'getEventStreams');

    const tick = async () => {
      let sawLive = false;
      try {
        const res = await getEventStreams({ eventId });
        if (cancelled) return;

        const streams = res.data?.streams || [];
        sawLive = streams.length > 0;

        if (sawLive) {
          // Read through a ref rather than nesting setSelectedStream inside a
          // setParticipants updater — updaters must stay pure or StrictMode's
          // double-invoke fires the selection twice.
          const prev = participantsRef.current;
          const merged = mergeLiveStreams(prev, streams);
          if (merged !== prev) {
            participantsRef.current = merged;
            setParticipants(merged);
            // Keep the viewer's POV; adopt a feed only if they had none.
            setSelectedStream((cur) => pickSelectedStream(cur, merged, merged));
          }
        }
      } catch (err) {
        // Offline, cold start, or the event isn't resolvable yet — keep polling.
        console.log('[EventWatch] stream re-resolve failed:', err?.message);
      } finally {
        if (!cancelled) {
          // Hunt fast while the screen is empty, idle back once feeds are up.
          // Fastest of all while the ceremony is holding its b-roll waiting to
          // hand off: that hold is the seam between the pre-event screens and
          // the live feeds, and 8s of granularity is long enough to feel like a
          // stall in what is meant to be one continuous sequence.
          const handingOff = ceremonyHandoffRef.current;
          timer = setTimeout(tick, sawLive ? 30000 : (handingOff ? 3000 : 8000));
        }
      }
    };

    tick();

    // Coming back to the tab should re-check immediately, not wait out the timer.
    const onVisible = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible' && !cancelled) {
        if (timer) clearTimeout(timer);
        tick();
      }
    };
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [eventId, isDemo]);

  // ============================================================================
  // BRACKET MATCHES - Load from Firestore subcollection
  // Populates both:
  //   - roundMatches (for MatchSelectorModal - matches in current round)
  //   - bracket (for BracketModal - full bracket visualization)
  // Path: tournaments/{eventId}/matches/{matchId} or matches/{eventId}/matches/{matchId}
  // ============================================================================
  useEffect(() => {
    if (!event || !eventId) return;

    // Determine which top-level collection this event lives in
    const collectionName = event._source === 'tournaments' ? 'tournaments' : 'matches';
    const bracketMatchesRef = collection(db, collectionName, eventId, 'matches');

    const unsub = onSnapshot(
      bracketMatchesRef,
      (snap) => {
        if (snap.empty) {
          setRoundMatches([]);
          return;
        }

        const allMatches = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort by round then matchNumber
        allMatches.sort((a, b) => (a.round || 0) - (b.round || 0) || (a.matchNumber || 0) - (b.matchNumber || 0));

        // ---- Populate roundMatches for MatchSelectorModal ----
        const activeRound = event.currentRound || 1;
        const currentRoundMatches = allMatches
          .filter(m => m.round === activeRound)
          .map(m => ({
            id: m.id,
            p1: {
              displayName: m.player1Name || m.p1Name || m.player1 || 'TBD',
              ign: m.player1Name || m.p1Name || null,
              isLive: m.status === 'live',
            },
            p2: {
              displayName: m.player2Name || m.p2Name || m.player2 || 'TBD',
              ign: m.player2Name || m.p2Name || null,
              isLive: m.status === 'live',
            },
            status: m.status,
          }));
        setRoundMatches(currentRoundMatches);

        // ---- Populate bracket for BracketModal (full bracket view) ----
        const roundMap = {};
        allMatches.forEach(m => {
          const r = m.round || 1;
          if (!roundMap[r]) roundMap[r] = [];
          roundMap[r].push(m);
        });

        const numRounds = event.totalRounds || Object.keys(roundMap).length;
        const bSize = event.bracketSize || event._rawData?.bracketSize;

        const rounds = Object.keys(roundMap)
          .sort((a, b) => Number(a) - Number(b))
          .map(roundNumStr => {
            const rNum = Number(roundNumStr);

            // Generate readable round name
            let roundName;
            if (rNum === numRounds) {
              roundName = 'Grand Final';
            } else if (rNum === numRounds - 1) {
              roundName = 'Semifinals';
            } else if (rNum === numRounds - 2) {
              roundName = 'Quarterfinals';
            } else if (bSize) {
              const playersInRound = bSize / Math.pow(2, rNum - 1);
              roundName = 'Round of ' + playersInRound;
            } else {
              roundName = 'Round ' + rNum;
            }

            return {
              name: roundName,
              matches: roundMap[roundNumStr].map(m => {
                const isP1Winner = m.winner != null && m.winner === m.player1;
                const isP2Winner = m.winner != null && m.winner === m.player2;

                let bracketStatus = 'upcoming';
                if (m.status === 'completed') bracketStatus = 'completed';
                else if (m.status === 'live') bracketStatus = 'in_progress';

                return {
                  id: m.id,
                  player1: {
                    name: m.player1Name || m.p1Name || m.player1 || 'TBD',
                    score: m.score1 || (isP1Winner ? 1 : 0),
                    winner: isP1Winner,
                  },
                  player2: {
                    name: m.player2Name || m.p2Name || m.player2 || 'TBD',
                    score: m.score2 || (isP2Winner ? 1 : 0),
                    winner: isP2Winner,
                  },
                  status: bracketStatus,
                };
              }),
            };
          });

        setBracket({ rounds });
      },
      (error) => {
        console.log('Error loading bracket matches:', error);
      }
    );

    return () => unsub();
  }, [event?.id, event?._source, event?.currentRound]);

  const totalPrize = useMemo(() => {
    // Prize pool = what performers compete for (potCents for matches,
    // netPoolCents for events). Flood boosts are real-money creator comp, a
    // separate currency — surfaced on their own, never summed into the pool.
    return event?.potCents || event?.live?.netPoolCents || event?.projected?.netPoolCents || 0;
  }, [event]);

  // Prevent white overscroll bar on mobile web by forcing black background
  // and disabling elastic bounce/overscroll on the document
  useEffect(() => {
    if (Platform.OS === 'web') {
      const html = document.documentElement;
      const body = document.body;
      const prevHtmlBg = html.style.backgroundColor;
      const prevBodyBg = body.style.backgroundColor;
      const prevHtmlOverscroll = html.style.overscrollBehavior;
      const prevBodyOverscroll = body.style.overscrollBehavior;
      const prevBodyOverflow = body.style.overflow;

      html.style.backgroundColor = '#000';
      body.style.backgroundColor = '#000';
      html.style.overscrollBehavior = 'none';
      body.style.overscrollBehavior = 'none';
      body.style.overflow = 'hidden';

      return () => {
        html.style.backgroundColor = prevHtmlBg;
        body.style.backgroundColor = prevBodyBg;
        html.style.overscrollBehavior = prevHtmlOverscroll;
        body.style.overscrollBehavior = prevBodyOverscroll;
        body.style.overflow = prevBodyOverflow;
      };
    }
  }, []);

  // Demo events keep a lively placeholder; real events show actual presence.
  const viewerCount = isDemo
    ? (event?.viewerCount || Math.max(participants.length * 10, 1))
    : liveViewerCount;
  const embedUrl = selectedStream ? getEmbedUrl(selectedStream.stream) : null;

  // ---- Event ceremony (pre/post b-roll) ---------------------------------
  // Everything between "registration closed" and "here are the Crits". Driven
  // by the real Match Ready lifecycle, not a wall clock — see
  // constants/ceremony.js. Ships behind settings/ceremony.enabled.
  // TRUE only once the player has actually painted a frame — not merely when a
  // playback URL resolved. The ceremony dissolves to reveal the video sitting
  // underneath it, so fading on "URL exists" would uncover a black element
  // still buffering HLS. Watch the element itself.
  const [feedPlaying, setFeedPlaying] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web') { setFeedPlaying(!!embedUrl); return undefined; }
    if (!embedUrl) { setFeedPlaying(false); return undefined; }
    // activeVideoEl, not videoRef: the double buffer swaps elements on a POV
    // switch. This effect re-binds at the flip (the incoming element has
    // painted by definition, so the priming line below holds feedPlaying
    // true), and the torn-down element's 'emptied' fires unheard.
    const video = activeVideoEl;
    if (!video) { setFeedPlaying(false); return undefined; }

    const mark = () => setFeedPlaying(true);
    const drop = () => setFeedPlaying(false);
    // readyState >= 2 means we already have current frame data.
    if (video.readyState >= 2 && !video.paused) setFeedPlaying(true);
    video.addEventListener('playing', mark);
    video.addEventListener('loadeddata', mark);
    video.addEventListener('emptied', drop);
    video.addEventListener('error', drop);
    return () => {
      video.removeEventListener('playing', mark);
      video.removeEventListener('loadeddata', mark);
      video.removeEventListener('emptied', drop);
      video.removeEventListener('error', drop);
    };
  }, [embedUrl, activeVideoEl]);

  // Keep every candidate POV's HLS muxer warm. The relay builds a channel's
  // muxer lazily on its FIRST manifest request and tears it down after ~60s
  // idle - so an unwatched racer's feed would greet a POV switch with a cold,
  // gap-padded window: no race-clock alignment possible and several extra
  // seconds before the first frame. One tiny manifest GET per feed per
  // interval (the same request any player makes) keeps the windows deep, so
  // switches land aligned and fast. Skipped while the tab is hidden.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const urls = participants
      .filter((p) => p.stream?.provider === 'critcash' && p.stream.playbackUrl)
      .map((p) => p.stream.playbackUrl);
    if (!urls.length) return undefined;
    const warm = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      for (const u of urls) fetch(u, { cache: 'no-store' }).catch(() => {});
    };
    warm();
    const iv = setInterval(warm, 40000); // muxer idle-close is ~60s
    return () => clearInterval(iv);
  }, [participants]);

  const ceremony = useEventCeremony(eventId, {
    hasLiveFeed: !!embedUrl,
    // Gate the dissolve on a painting picture, so the handoff is a true
    // crossfade rather than b-roll -> black -> video.
    feedReady: feedPlaying,
    selectedUid: selectedStream?.odUserId || null,
    enabled: !!eventId && !isDemo,
  });
  ceremonyHandoffRef.current = ceremony.phase === 'handoff' && !feedPlaying;

  // Switch camera off a racer whose run has ended (founder step 5). Prefers
  // someone still racing; falls back to any other resolvable feed.
  const switchToAnotherCamera = useCallback(() => {
    const currentUid = selectedStream?.odUserId;
    const others = participantsRef.current.filter(
      (p) => p.odUserId !== currentUid && getEmbedUrl(p.stream),
    );
    if (!others.length) return;
    const stillRacing = others.find((p) => {
      const row = (ceremony.field || []).find((f) => f.uid === p.odUserId);
      return !row || row.state === 'racing';
    });
    setSelectedStream(stillRacing || others[0]);
  }, [selectedStream, ceremony.field]);

  const canSwitchCamera = participants.some(
    (p) => p.odUserId !== selectedStream?.odUserId && getEmbedUrl(p.stream),
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
        </View>
      </SafeAreaView>
    );
  }

  if (!event) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>Event not found</Text>
          <Pressable style={styles.backBtnError} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
            <Text style={styles.backBtnErrorText}>{'<'} Go Back</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.watchLayout}>
        {/* Main Content Row - Video + Chat side by side */}
        <View style={[styles.mainContentRow, !isWide && styles.mainContentStack]}>
          {/* Video Column */}
          <View style={styles.videoColumn} nativeID="video-column">
            {/* Floating Top Bar */}
            <View style={styles.floatingTopBar}>
              <Pressable style={styles.backBtn} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
                <Text style={styles.backBtnLabel}>BACK</Text>
              </Pressable>

              <View style={styles.eventTitleBar}>
                {/* The red dot used to render for every status, including a
                    finished event. Now that a completed event shows its
                    standings underneath, an unqualified LIVE next to them
                    would be a plain untruth. */}
                {event.status !== 'completed'
                  && event.status !== 'cancelled'
                  && event.status !== 'canceled' && (
                  <View style={styles.liveIndicator}>
                    <View style={styles.liveDot} />
                  </View>
                )}
                <Text style={styles.eventName} numberOfLines={1}>
                  {event.title || event.name}
                </Text>
                {isDemo && (
                  <View style={styles.demoBadge}>
                    <Text style={styles.demoBadgeText}>DEMO</Text>
                  </View>
                )}
              </View>

              <View style={styles.viewerBadge}>
                <ViewerIcon size={14} color="#e11900" />
                <Text style={styles.viewerCountText}>{viewerCount.toLocaleString()}</Text>
              </View>
            </View>

            {/* Main Video Area */}
            <View 
              style={styles.videoArea} 
              nativeID="video-area"
              {...(Platform.OS === 'web' ? {
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                onMouseMove: handleMouseMove,
              } : {})}
            >
              {embedUrl ? (
                selectedStream?.stream?.provider === 'critcash' ? (
                  // CritCash HLS stream — double-buffered <video> + hls.js
                  <LiveHlsPlayer
                    src={embedUrl}
                    muted={isMuted}
                    videoRef={videoRef}
                    onActiveVideoChange={(el) => {
                      setActiveVideoEl(el);
                      // A committed flip always autoplays the incoming feed;
                      // resync the pause icon so it can't show stale state.
                      if (el) setIsPaused(el.paused);
                    }}
                    onAutoplayBlocked={() => setIsMuted(true)}
                    onSwitchFailed={(failedSrc, activeSrc) => {
                      // The target feed never painted (dead stream): the
                      // picture is still the previous racer, so pull the
                      // overlay chip back to whoever is actually showing
                      // rather than leave name and picture disagreeing.
                      if (!activeSrc) return;
                      setSelectedStream((prev) => {
                        const showing = participantsRef.current.find(
                          (p) => getEmbedUrl(p.stream) === activeSrc,
                        );
                        return showing || prev;
                      });
                    }}
                  />
                ) : (
                   // Twitch/YouTube embed - use EmbedPlayer for web compatibility
                   <EmbedPlayer 
                     embedUrl={embedUrl} 
                     provider={selectedStream?.stream?.provider} 
                   />
                 )
              ) : (
                <View style={styles.videoPlaceholder}>
                  {/* "Select a stream" told spectators to do something they
                      can't when the real state is that nobody is streaming yet.
                      Say which one it is. */}
                  <Text style={styles.videoPlaceholderText}>
                    {participants.length > 0
                      ? 'Select a stream to watch'
                      : 'Waiting for players to go live'}
                  </Text>
                </View>
              )}

              {/* Video Player Overlay Controls */}
              <VideoPlayerOverlay
                visible={showControls}
                isPaused={isPaused}
                isMuted={isMuted}
                onPlayPause={() => {
                  const video = videoRef.current;
                  if (video) {
                    if (video.paused) { video.play?.().catch(() => {}); setIsPaused(false); }
                    else { video.pause?.(); setIsPaused(true); }
                  } else {
                    setIsPaused(p => !p);
                  }
                }}
                onMuteToggle={() => {
                  // State only: isMuted is the single source of truth and
                  // LiveHlsPlayer applies it to whichever element is active.
                  // Poking videoRef here would race the double buffer's flip.
                  setIsMuted(m => !m);
                }}
                selectedPlayer={selectedStream}
                opponent={opponent}
                participants={participants}
                onSwitchPlayer={setSelectedStream}
                onOpenMatchSelector={() => setMatchSelectorVisible(true)}
                currentMatch={currentMatch}
                onReport={selectedStream?.odUserId
                  ? () => openReport('stream', { uid: selectedStream.odUserId, name: selectedStream.displayName || 'this player' })
                  : null}
              />

              {/* Event ceremony — the pre/post b-roll. Sits over the player and
                  its controls, under CompactInfoBar. Renders nothing at all
                  outside the ceremony window, so an ordinary live event is
                  untouched. */}
              <CeremonyStage
                ceremony={ceremony}
                eventId={eventId}
                onSwitchCamera={switchToAnotherCamera}
                canSwitchCamera={canSwitchCamera}
              />

              {/* CritCash Watermark - Bottom Right */}
              <View style={styles.watermarkContainer} pointerEvents="none">
                <Image 
                  source={{ uri: WATERMARK_URL }} 
                  style={styles.watermark}
                  resizeMode="contain"
                />
              </View>

              {/* POV selection lives in the player controls: the current racer
                  chip + "More v" (full racer picker on FFA events). The old
                  thumb rail is gone — 16 thumbs walled off the whole player. */}

              {/* Compact Info Bar */}
              <CompactInfoBar
                event={event}
                critsEnabled={critsEnabled}
                rumbleOpen={rumble.open}
                rumbleCount={rumbleCount}
                onExpand={() => setSidebarExpanded(true)}
                onBracket={() => setBracketVisible(true)}
                onChat={() => setChatVisible(!chatVisible)}
                onFlood={() => {
                  setFloodModalVisible(true);
                  // Fresh saved-card / today's-allowance state for the picker.
                  rumble.refresh();
                }}
                onFullscreen={toggleFullscreen}
                isFullscreen={isFullscreen}
                onMatchSelect={() => setMatchSelectorVisible(true)}
                chatOpen={chatVisible}
                unreadCount={0}
              />
            </View>
          </View>

          {/* Chat Panel - Side by side with video */}
          <ChatPanel
            visible={chatVisible}
            onClose={() => setChatVisible(false)}
            eventId={eventId}
            eventTitle={event.title}
            isDemo={isDemo}
            isLoggedIn={isLoggedIn}
            onRequireAuth={() => { setChatVisible(false); navigation.navigate('SignUp'); }}
            onUserPress={(msg) => setChatUserCard({ user: msg.user, userId: msg.userId, messageId: msg.id, messageText: msg.message })}
            hiddenUids={hiddenChatUids}
            width={isWide ? chatWidth : '100%'}
            stacked={!isWide}
          />
        </View>

        {/* Match Selector Modal */}
        <MatchSelectorModal
          visible={matchSelectorVisible}
          onClose={() => setMatchSelectorVisible(false)}
          matches={roundMatches}
          currentMatchId={selectedMatchId || eventId}
          onSelectMatch={(match) => {
            // Navigate to the selected match or load it
            setSelectedMatchId(match.id);
            // You could navigate or reload participants here
          }}
          roundNumber={event?.currentRound || 1}
        />

        {/* Rumble Modal + Ticker — real-money Rumbles, money-free display */}
        {FLOOD_ENABLED && (
          <>
            {/* Screen-level burst: portals to body (or the fullscreen element),
                paints over the ceremony (z-99999 vs videoArea's z-14). */}
            <RumbleBurst
              visible={!!rumbleBurst}
              milestone={rumbleBurst}
              subtitle={rumbleBurst ? `The crowd hit Level ${rumbleMeterLevel(rumbleBurst)}!` : undefined}
              onDone={() => setRumbleBurst(0)}
            />

            <FloodModal
              visible={floodModalVisible}
              onClose={() => setFloodModalVisible(false)}
              eventId={eventId}
              eventTitle={event?.title}
              collection={event?._source}
              onRequireAuth={() => { setFloodModalVisible(false); navigation.navigate('SignUp'); }}
              onVerify={() => { setFloodModalVisible(false); navigation.navigate('IdVerification'); }}
              rumble={rumble}
            />

            {/* Wave rows (3.3: {displayName, wave}, no amount); legacy rows
                carry amountCents, shown as their wave — never the amount. */}
            {floodContributions.length > 0 && (
              <View style={styles.tickerBar}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tickerContent}>
                  {[...floodContributions, ...floodContributions].map((c, i) => (
                    <View key={`${c.id}-${i}`} style={styles.tickerItem}>
                      <Text style={styles.tickerWave}>~</Text>
                      <Text style={styles.tickerUser}>{c.anonymous ? 'Anonymous' : (c.displayName || 'A viewer')}</Text>
                      <Text style={styles.tickerAction}>sent a</Text>
                      <Text style={styles.tickerAmount}>{waveLabel(rumbleRowWave(c))} Rumble</Text>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
          </>
        )}

        {/* Chat user card — tap a handle in event chat: profile / hide / report */}
        <Modal visible={!!chatUserCard} transparent animationType="fade" onRequestClose={() => setChatUserCard(null)}>
          <Pressable style={styles.modBackdrop} onPress={() => setChatUserCard(null)}>
            <Pressable style={styles.modUserCard} onPress={(e) => e.stopPropagation()}>
              {chatUserCard ? (
                <>
                  <View style={styles.modUserCardHeader}>
                    <Text style={styles.modUserCardName}>{chatUserCard.user}</Text>
                  </View>
                  <Pressable style={styles.modUserCardAction} onPress={() => { setChatUserCard(null); openProfile(chatUserCard.user); }}>
                    <Text style={styles.modUserCardActionText}>View profile</Text>
                  </Pressable>
                  {chatUserCard.userId !== auth.currentUser?.uid ? (
                    <>
                      <Pressable style={styles.modUserCardAction} onPress={() => toggleHideChatUser(chatUserCard.userId)}>
                        <Text style={styles.modUserCardActionText}>
                          {hiddenChatUids.has(chatUserCard.userId) ? 'Show their messages' : 'Hide their messages (just for you)'}
                        </Text>
                      </Pressable>
                      <Pressable
                        style={styles.modUserCardAction}
                        onPress={() => openReport('chat_user', {
                          uid: chatUserCard.userId, name: chatUserCard.user,
                          messageId: chatUserCard.messageId, messageText: chatUserCard.messageText,
                        })}
                      >
                        <Text style={[styles.modUserCardActionText, { color: '#e05555' }]}>Report</Text>
                      </Pressable>
                    </>
                  ) : null}
                </>
              ) : null}
            </Pressable>
          </Pressable>
        </Modal>

        {/* Report modal — participant feeds and chat users; reports go straight to admins */}
        <Modal visible={!!reportTarget} transparent animationType="fade" onRequestClose={() => setReportTarget(null)}>
          <Pressable style={styles.modBackdrop} onPress={() => setReportTarget(null)}>
            <Pressable style={styles.modCard} onPress={(e) => e.stopPropagation()}>
              {reportStatus === 'sent' ? (
                <View style={{ alignItems: 'center', paddingVertical: 18 }}>
                  <Text style={styles.modSentText}>Report sent — our team will review. ✓</Text>
                </View>
              ) : reportTarget ? (
                <>
                  <Text style={styles.modTitle}>
                    Report {reportTarget.kind === 'stream' ? `${reportTarget.name}'s feed` : reportTarget.name}
                  </Text>
                  <Text style={styles.modSub}>
                    Tell us what's wrong — reports go straight to the CritCash team.
                  </Text>
                  <View style={styles.modReportChips}>
                    {[
                      ['inappropriate', 'Inappropriate'],
                      ['harassment', 'Harassment'],
                      ['cheating', 'Cheating'],
                      ['spam', 'Spam'],
                      ['other', 'Other'],
                    ].map(([key, label]) => (
                      <Pressable
                        key={key}
                        style={[styles.modReportChip, reportReason === key && styles.modReportChipActive]}
                        onPress={() => setReportReason(key)}
                      >
                        <Text style={[styles.modReportChipText, reportReason === key && styles.modReportChipTextActive]}>
                          {label}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                  <TextInput
                    style={styles.modDetailsInput}
                    placeholder="Anything else we should know? (optional)"
                    placeholderTextColor="#666"
                    value={reportDetails}
                    onChangeText={setReportDetails}
                    maxLength={500}
                    multiline
                  />
                  {reportStatus ? <Text style={styles.modErrorText}>{reportStatus}</Text> : null}
                  <Pressable
                    style={[styles.modSendBtn, reportBusy && { opacity: 0.6 }]}
                    onPress={submitReport}
                    disabled={reportBusy}
                  >
                    <Text style={styles.modSendBtnText}>{reportBusy ? 'Sending…' : 'Send report'}</Text>
                  </Pressable>
                </>
              ) : null}
            </Pressable>
          </Pressable>
        </Modal>

        {/* Expanded Sidebar Modal */}
        <Modal visible={sidebarExpanded} animationType="slide" transparent>
          <View style={styles.sidebarModalOverlay}>
            <Pressable style={styles.sidebarModalDismiss} onPress={() => setSidebarExpanded(false)} />
            <View style={styles.sidebarModal}>
              <View style={styles.sidebarHandle} />
              
              <ScrollView showsVerticalScrollIndicator={false}>
                {/* Prize Pool + Tsunami Combined */}
                <View style={styles.tsunamiSection}>
                  <View style={styles.prizeHeader}>
                    {critsEnabled && (
                    <>
                    <Text style={styles.sectionLabel}>CRIT POOL</Text>
                    <Text style={styles.prizeTotal}>{formatEventPrize(event, totalPrize)}</Text>
                    </>
                    )}
                    {FLOOD_ENABLED && rumbleCount > 0 && (
                      <Text style={styles.prizeFlood}>
                        {`~ ${formatRumbleCount(rumbleCount)} ${rumbleCount === 1 ? 'Rumble' : 'Rumbles'} from the crowd`}
                      </Text>
                    )}
                  </View>

                  {/* Integrated Tsunami Meter — shown while Rumbles are open
                      for this viewer, or once the event has any (so a dark /
                      closed event without Rumbles shows no meter at all), and
                      kept once shown for this event (showSidebarRumbleMeter) */}
                  {showSidebarRumbleMeter && (
                    <TsunamiMeter
                      eventId={eventId}
                      collection={event?._source}
                      compact
                      showContributions={false}
                      onRequireAuth={() => { setSidebarExpanded(false); navigation.navigate('SignUp'); }}
                      onVerify={() => { setSidebarExpanded(false); navigation.navigate('IdVerification'); }}
                      rumble={rumble}
                    />
                  )}
                </View>

                {/* Current Match */}
                {currentMatch && (
                  <View style={styles.matchSection}>
                    <Text style={styles.sectionLabel}>CURRENT MATCH - {currentMatch.roundName}</Text>
                    <View style={styles.matchCard}>
                      <View style={styles.matchPlayer}>
                        <Text style={styles.matchPlayerName}>{currentMatch.player1?.displayName}</Text>
                        <Text style={styles.matchPlayerRecord}>{currentMatch.player1?.wins}-{currentMatch.player1?.losses}</Text>
                      </View>
                      <View style={styles.matchScoreBox}>
                        <Text style={[styles.matchScore, currentMatch.score1 > currentMatch.score2 && styles.matchScoreWin]}>
                          {currentMatch.score1}
                        </Text>
                        <Text style={styles.matchScoreDash}>-</Text>
                        <Text style={[styles.matchScore, currentMatch.score2 > currentMatch.score1 && styles.matchScoreWin]}>
                          {currentMatch.score2}
                        </Text>
                      </View>
                      <View style={styles.matchPlayer}>
                        <Text style={styles.matchPlayerName}>{currentMatch.player2?.displayName}</Text>
                        <Text style={styles.matchPlayerRecord}>{currentMatch.player2?.wins}-{currentMatch.player2?.losses}</Text>
                      </View>
                    </View>
                  </View>
                )}

                {/* Bracket Button */}
                <Pressable 
                  style={styles.viewBracketBtn} 
                  onPress={() => {
                    setSidebarExpanded(false);
                    setBracketVisible(true);
                  }}
                >
                  <Text style={styles.viewBracketBtnText}>View Full Bracket</Text>
                  <Text style={styles.viewBracketArrow}>{'>'}</Text>
                </Pressable>

                {/* Quick Stats */}
                <View style={styles.quickStats}>
                  <View style={styles.quickStat}>
                    <Text style={styles.quickStatValue}>{event.entrantsCount || 0}</Text>
                    <Text style={styles.quickStatLabel}>Entrants</Text>
                  </View>
                  <View style={styles.quickStat}>
                    <Text style={styles.quickStatValue}>{viewerCount.toLocaleString()}</Text>
                    <Text style={styles.quickStatLabel}>Viewers</Text>
                  </View>
                  <View style={styles.quickStat}>
                    <Text style={styles.quickStatValue}>{event.currentRound}/{event.totalRounds}</Text>
                    <Text style={styles.quickStatLabel}>Round</Text>
                  </View>
                </View>
              </ScrollView>
            </View>
          </View>
        </Modal>

        {/* Bracket Modal */}
        <BracketModal
          visible={bracketVisible}
          onClose={() => setBracketVisible(false)}
          bracket={bracket}
          currentMatch={currentMatch}
          eventTitle={event.title}
        />
      </View>
    </SafeAreaView>
  );
}

// ---------- Styles ----------
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#000',
    overflow: 'hidden',
    // WEB: the document chain gives body/#root only `min-height:100%`, so an
    // unbroken `flex:1` chain resolves against an INDEFINITE height and every
    // box becomes content-sized. The chat list then grows the page instead of
    // scrolling inside its own box, and the growing row pushes the video down.
    // Pinning the screen to the viewport makes the chain definite, which is
    // what makes ScrollView's overflow-y actually engage.
    // Same lock StreamWatchScreen/TeamStreamScreen already ship (containerLocked).
    // maxHeight is the load-bearing half: safeArea is a flex item, so plain
    // `height` would be ignored by the flex algorithm — maxHeight clamps after.
    ...(Platform.OS === 'web' ? { height: '100vh', maxHeight: '100vh' } : null),
  },
  watchLayout: {
    flex: 1,
    overflow: 'hidden',
  },
  mainContentRow: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  mainContentStack: {
    flexDirection: 'column',
  },
  videoColumn: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#888',
    fontSize: 16,
    marginBottom: 16,
  },
  backBtnError: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
  },
  backBtnErrorText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '600',
  },

  // Floating Top Bar
  floatingTopBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 100%)',
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.6)',
    gap: 6,
  },
  backBtnArrow: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  backBtnLabel: {
    color: '#FCD000',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  backBtnText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  eventTitleBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginHorizontal: 12,
  },
  liveIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e11900',
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 4,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#fff',
  },
  liveDotSmall: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#fff',
  },
  eventName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    maxWidth: 200,
  },
  demoBadge: {
    backgroundColor: '#FCD000',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  demoBadgeText: {
    color: '#000',
    fontSize: 9,
    fontWeight: '900',
  },
  viewerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    gap: 4,
  },
  viewerIcon: {
    color: '#e11900',
    fontSize: 10,
    fontWeight: '700',
  },
  viewerCount: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  viewerCountText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  viewerText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },

  // Video Area
  videoArea: {
    flex: 1,
    backgroundColor: '#000',
    overflow: 'hidden',
  },
  videoPlayer: {
    flex: 1,
  },
  videoPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0a0a0a',
  },
  videoPlaceholderText: {
    color: '#444',
    fontSize: 14,
  },

  // Watermark
  watermarkContainer: {
    position: 'absolute',
    bottom: 70,
    right: 12,
    opacity: 0.7,
  },
  watermark: {
    width: 80,
    height: 40,
  },

  // Demo Video
  demoVideoContainer: {
    flex: 1,
    backgroundColor: '#0a0a0a',
    alignItems: 'center',
    justifyContent: 'center',
  },
  demoVideoInner: {
    alignItems: 'center',
  },
  demoGameAbbr: {
    color: '#222',
    fontSize: 80,
    fontWeight: '900',
    letterSpacing: 8,
  },
  demoEventTitle: {
    color: '#fff',
    fontSize: 28,
    fontWeight: '800',
    marginTop: 16,
  },
  demoBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
  demoLiveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e11900',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    gap: 5,
  },
  demoLiveText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '900',
  },
  demoViewers: {
    color: '#666',
    fontSize: 14,
  },
  demoNote: {
    color: '#333',
    fontSize: 11,
    marginTop: 24,
    fontStyle: 'italic',
  },

  // Stream Switcher
  streamSwitcher: {
    position: 'absolute',
    bottom: 70,
    left: 12,
    right: 12,          // full-grid rail scrolls; cap it to the player width
    maxHeight: 84,
  },
  streamThumb: {
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderWidth: 2,
    borderColor: 'transparent',
    minWidth: 60,
  },
  streamThumbActive: {
    borderColor: '#FCD000',
    backgroundColor: 'rgba(252, 208, 0, 0.15)',
  },
  streamThumbAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    position: 'relative',
    marginBottom: 4,
  },
  streamThumbAvatarImg: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  streamThumbAvatarPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#333',
    alignItems: 'center',
    justifyContent: 'center',
  },
  streamThumbInitials: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  streamThumbLiveDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e11900',
    borderWidth: 2,
    borderColor: '#000',
  },
  streamThumbInner: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  streamThumbIcon: {
    fontSize: 14,
  },
  streamThumbName: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    maxWidth: 60,
  },
  streamThumbNameActive: {
    color: '#FCD000',
  },
  streamThumbMore: {
    width: 42,
    height: 42,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  streamThumbMoreText: {
    color: '#888',
    fontSize: 11,
    fontWeight: '700',
  },

  // Compact Info Bar
  compactBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0,0,0,0.85)',
    paddingVertical: 10,
    paddingHorizontal: 16,
    zIndex: 20, // Above everything else
  },
  compactLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  compactRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  compactItem: {
    alignItems: 'center',
  },
  compactLabel: {
    color: '#666',
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  compactValue: {
    color: '#FCD000',
    fontSize: 16,
    fontWeight: '900',
  },
  compactValueFlood: {
    color: '#4ade80',
    fontSize: 14,
    fontWeight: '800',
  },
  compactFloodBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(74, 222, 128, 0.4)',
    gap: 6,
  },
  compactFloodIcon: {
    color: '#4ade80',
    fontSize: 14,
    fontWeight: '700',
  },
  compactFloodLabel: {
    color: '#4ade80',
    fontSize: 12,
    fontWeight: '600',
  },
  compactFloodAmount: {
    color: '#4ade80',
    fontSize: 11,
    fontWeight: '600',
  },
  compactSpacer: {
    flex: 1,
  },
  compactChatBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    gap: 6,
    position: 'relative',
  },
  compactChatBtnActive: {
    backgroundColor: 'rgba(225, 25, 0, 0.3)',
    borderWidth: 1,
    borderColor: '#e11900',
  },
  compactChatIcon: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  compactChatLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  compactChatBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#e11900',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  compactChatBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
  },
  compactFullscreenBtnActive: {
    backgroundColor: 'rgba(252,208,0,0.12)',
  },
  compactFullscreenBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactFullscreenIcon: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  compactExpandBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#222',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactExpandIcon: {
    color: '#fff',
    fontSize: 16,
  },
  
  // Match Selector Button
  compactMatchBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    gap: 6,
  },
  compactMatchIcon: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  compactMatchLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  
  // Updated Flood Button (blue wave)
  compactFloodWave: {
    color: '#3b82f6',
    fontSize: 16,
    fontWeight: '700',
  },
  
  // Chat Bubble Icon
  compactChatBubble: {
    width: 20,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactChatDots: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    marginTop: -2,
  },

  // Video Player Overlay
  playerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: COMPACT_BAR_HEIGHT, // Stop above the compact bar (was 50 — 10px short, so
                                // the bottom control row sat under the bar and clipped)
    justifyContent: 'space-between',
    zIndex: 10,
    pointerEvents: 'box-none', // Allow clicks to pass through to elements below
  },
  playerOverlayTop: {
    paddingTop: 12,
    paddingHorizontal: 16,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
  },
  playerOverlayTopContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  centerPlayBtn: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -30 }, { translateY: -30 }],
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerPlayIcon: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
  },
  playerOverlayBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(0,0,0,0.7)',
  },
  playerControlsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  playerControlsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  reportFlagGlyph: { color: '#bbb', fontSize: 15, fontWeight: '700' },

  // Moderation modals (user card + report — parity with StreamWatch)
  modBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modUserCard: {
    width: '100%', maxWidth: 320, backgroundColor: '#111',
    borderColor: '#222', borderWidth: 1, borderRadius: 14, padding: 8,
  },
  modUserCardHeader: {
    paddingVertical: 10, paddingHorizontal: 12,
    borderBottomWidth: 1, borderBottomColor: '#222', marginBottom: 4,
  },
  modUserCardName: { color: '#fff', fontSize: 15, fontWeight: '800' },
  modUserCardAction: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 8 },
  modUserCardActionText: { color: '#ccc', fontSize: 13.5, fontWeight: '600' },
  modCard: {
    width: '100%', maxWidth: 380, backgroundColor: '#111',
    borderColor: '#222', borderWidth: 1, borderRadius: 14, padding: 20,
  },
  modTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  modSub: { color: '#999', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  modReportChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  modReportChip: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, paddingVertical: 7, paddingHorizontal: 12,
  },
  modReportChipActive: { backgroundColor: '#2a0d0d', borderColor: '#e05555' },
  modReportChipText: { color: '#bbb', fontSize: 12, fontWeight: '700' },
  modReportChipTextActive: { color: '#ff8a8a' },
  modDetailsInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: '#eee', fontSize: 13,
    paddingVertical: 8, paddingHorizontal: 12, marginTop: 12,
    minHeight: 60, textAlignVertical: 'top',
  },
  modErrorText: { color: '#e05555', fontSize: 12, marginTop: 10, textAlign: 'center' },
  modSendBtn: {
    backgroundColor: '#FCD000', borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', marginTop: 14,
  },
  modSendBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  modSentText: { color: '#FCD000', fontSize: 15, fontWeight: '800' },
  playerControlBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playerControlIcon: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  liveIndicatorSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e11900',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    gap: 4,
  },
  liveDotSmall: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#fff',
  },
  liveTextSmall: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  
  // Channel Switcher
  channelSwitcher: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 20,
    padding: 4,
    gap: 4,
  },
  channelBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.1)',
    gap: 6,
  },
  channelBtnActive: {
    backgroundColor: '#FCD000',
  },
  channelLiveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e11900',
  },
  channelOfflineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#444',
  },
  channelOfflineIcon: {
    color: '#666',
    fontSize: 10,
  },
  channelBtnText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    maxWidth: 80,
  },
  channelBtnTextActive: {
    color: '#000',
  },
  moreMatchesBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.05)',
    gap: 4,
  },
  // Racer POV picker popover (FFA events) — anchored above the More button
  povMenu: {
    position: 'absolute',
    bottom: 44,
    right: 0,
    minWidth: 190,
    backgroundColor: 'rgba(10,10,10,0.96)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    paddingVertical: 6,
    zIndex: 40,
  },
  povMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  povMenuItemActive: {
    backgroundColor: 'rgba(252,208,0,0.12)',
  },
  moreMatchesBtnText: {
    color: '#888',
    fontSize: 11,
    fontWeight: '600',
  },
  moreMatchesArrow: {
    color: '#888',
    fontSize: 10,
  },

  // Match Selector Modal
  matchSelectorOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  matchSelectorContainer: {
    width: '90%',
    maxWidth: 400,
    maxHeight: '70%',
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    overflow: 'hidden',
  },
  matchSelectorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#0a0a0a',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  matchSelectorTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  matchSelectorClose: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  matchSelectorCloseText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  matchSelectorList: {
    flex: 1,
    padding: 8,
  },
  matchSelectorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    marginBottom: 8,
    backgroundColor: '#222',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
  },
  matchSelectorItemActive: {
    borderColor: '#FCD000',
    backgroundColor: 'rgba(252, 208, 0, 0.1)',
  },
  matchSelectorItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  matchNumber: {
    color: '#666',
    fontSize: 12,
    fontWeight: '700',
    width: 30,
  },
  matchPlayers: {
    flex: 1,
  },
  matchPlayerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  matchLiveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#e11900',
  },
  matchPlayerName: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '500',
  },
  matchVs: {
    color: '#666',
    fontSize: 10,
    fontWeight: '600',
    marginVertical: 2,
  },
  matchSelectorItemRight: {
    marginLeft: 12,
  },
  matchLiveBadge: {
    backgroundColor: '#e11900',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  matchLiveText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  matchWaitingText: {
    color: '#666',
    fontSize: 11,
    fontWeight: '500',
  },
  matchSelectorEmpty: {
    padding: 32,
    alignItems: 'center',
  },
  matchSelectorEmptyText: {
    color: '#666',
    fontSize: 14,
  },

  // Flood Modal
  floodModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'flex-end',
  },
  floodModalContainer: {
    backgroundColor: '#111',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  floodModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: '#0a0a0a',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  floodModalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  floodModalTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '800',
  },
  floodModalClose: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  floodModalCloseText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  floodModalEventInfo: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  floodModalEventTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  floodModalEventSubtitle: {
    color: '#3b82f6',
    fontSize: 12,
    marginTop: 4,
  },
  floodModalContent: {
    padding: 16,
  },

  // Ticker
  tickerBar: {
    backgroundColor: '#0a0a0a',
    borderTopWidth: 1,
    borderTopColor: '#1a1a1a',
    paddingVertical: 8,
  },
  tickerContent: {
    flexDirection: 'row',
    gap: 32,
    paddingHorizontal: 12,
  },
  tickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  tickerWave: {
    fontSize: 12,
  },
  tickerUser: {
    fontSize: 11,
    color: '#888',
  },
  tickerAction: {
    fontSize: 11,
    color: '#555',
  },
  tickerAmount: {
    fontSize: 11,
    fontWeight: '700',
    color: '#4ade80',
  },

  // Chat Panel (side by side with video)
  // Width is applied inline from useWindowDimensions — it changes with the
  // viewport, so it cannot live in the frozen stylesheet.
  chatPanel: {
    backgroundColor: '#111',
    borderLeftWidth: 1,
    borderLeftColor: '#222',
    minHeight: 0,
  },
  // Narrow viewports stack chat UNDER the video instead of beside it. A fixed
  // 300px chat column next to the video left ~75px of video on a phone.
  chatPanelStacked: {
    flex: 1,
    borderLeftWidth: 0,
    borderTopWidth: 1,
    borderTopColor: '#222',
  },
  chatHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  chatTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '800',
  },
  chatSubtitle: {
    color: '#666',
    fontSize: 11,
    marginTop: 2,
  },
  chatCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#222',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatCloseBtnText: {
    color: '#fff',
    fontSize: 16,
  },
  chatMessages: {
    flex: 1,
    // Without this a flex child refuses to shrink below its content height, so
    // the list grows the panel instead of scrolling inside it.
    minHeight: 0,
  },
  chatMessagesContent: {
    padding: 12,
    gap: 8,
  },
  chatNewMsgs: {
    position: 'absolute',
    bottom: 8,
    alignSelf: 'center',
    backgroundColor: '#FCD000',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  chatNewMsgsText: {
    color: '#0b0b0b',
    fontSize: 11,
    fontWeight: '700',
  },
  chatMsg: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chatMsgUser: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '700',
  },
  chatMsgText: {
    color: '#ddd',
    fontSize: 13,
    flex: 1,
  },
  chatMsgSystem: {
    backgroundColor: 'rgba(252, 208, 0, 0.1)',
    borderRadius: 8,
    padding: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#FCD000',
  },
  chatMsgSystemText: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '600',
  },
  chatInputContainer: {
    flexDirection: 'row',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#222',
    gap: 8,
  },
  chatSignupCta: {
    margin: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#FCD000',
    borderRadius: 12,
    alignItems: 'center',
  },
  chatSignupCtaText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '800',
  },
  chatSignupCtaSub: {
    color: '#1a1a1a',
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
    textAlign: 'center',
  },
  chatInput: {
    flex: 1,
    height: 40,
    backgroundColor: '#1a1a1a',
    borderRadius: 20,
    paddingHorizontal: 16,
    color: '#fff',
    fontSize: 14,
  },
  chatSendBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FCD000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatSendBtnDisabled: {
    backgroundColor: '#333',
  },
  chatSendBtnText: {
    color: '#000',
    fontSize: 18,
    fontWeight: '700',
  },
  chatSendDie: {
    width: 24,
    height: 24,
  },

  // Sidebar Modal
  sidebarModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  sidebarModalDismiss: {
    flex: 1,
  },
  sidebarModal: {
    backgroundColor: '#111',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: 24,
  },
  sidebarHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#333',
    borderRadius: 2,
    alignSelf: 'center',
    marginVertical: 12,
  },

  // Sections
  sectionLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#666',
    letterSpacing: 0.5,
    marginBottom: 8,
  },

  // Tsunami Section
  tsunamiSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  prizeHeader: {
    marginBottom: 16,
  },
  prizeTotal: {
    fontSize: 36,
    fontWeight: '900',
    color: '#FCD000',
  },
  prizeFlood: {
    fontSize: 13,
    fontWeight: '600',
    color: '#4ade80',
    marginTop: 4,
  },
  demoTsunami: {
    gap: 12,
  },
  tsunamiBarBg: {
    height: 12,
    backgroundColor: '#222',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tsunamiBarFill: {
    height: '100%',
    backgroundColor: '#00bfff',
    borderRadius: 6,
  },
  tsunamiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tsunamiProgress: {
    color: '#888',
    fontSize: 12,
  },
  tsunamiNext: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '600',
  },
  addToPrizeBtn: {
    backgroundColor: '#4ade80',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  addToPrizeBtnText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '800',
  },

  // Match Section
  matchSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  matchCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0a0a0a',
    borderRadius: 12,
    padding: 12,
  },
  matchPlayer: {
    flex: 1,
    alignItems: 'center',
  },
  matchPlayerName: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },
  matchPlayerRecord: {
    color: '#666',
    fontSize: 11,
    marginTop: 2,
  },
  matchScoreBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
  },
  matchScore: {
    color: '#fff',
    fontSize: 28,
    fontWeight: '900',
  },
  matchScoreWin: {
    color: '#4ade80',
  },
  matchScoreDash: {
    color: '#444',
    fontSize: 20,
  },

  // View Bracket Button
  viewBracketBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: 16,
    marginVertical: 12,
    padding: 14,
    backgroundColor: '#1a1a1a',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
  },
  viewBracketBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  viewBracketArrow: {
    color: '#FCD000',
    fontSize: 16,
    fontWeight: '700',
  },

  // Quick Stats
  quickStats: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    gap: 8,
  },
  quickStat: {
    flex: 1,
    backgroundColor: '#0a0a0a',
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  quickStatValue: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '800',
  },
  quickStatLabel: {
    color: '#666',
    fontSize: 10,
    marginTop: 2,
  },

  // Modal Overlay
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
  },
  bracketModalContainer: {
    flex: 1,
    margin: 16,
    backgroundColor: '#111',
    borderRadius: 16,
    overflow: 'hidden',
  },
  bracketModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  bracketModalTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '800',
  },
  bracketModalSubtitle: {
    color: '#888',
    fontSize: 12,
    marginTop: 2,
  },
  modalCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#222',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCloseBtnText: {
    color: '#fff',
    fontSize: 18,
  },

  // Bracket Grid - New with connecting lines
  bracketScrollV: {
    flex: 1,
    backgroundColor: '#0a0a0a',
  },
  bracketScrollVContent: {
    padding: 20,
    paddingBottom: 40,
  },
  bracketScrollH: {
    paddingRight: 40,
  },
  bracketGridNew: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  bracketRoundWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  bracketRoundLabel: {
    color: '#666',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    position: 'absolute',
    top: -18,
    left: 0,
    width: 140,
    textAlign: 'center',
  },
  bracketRoundMatches: {
    width: 140,
  },
  bracketMatchWrapper: {
    height: 56,
  },
  bracketMatchBox: {
    width: 140,
    height: 56,
    backgroundColor: '#1a1a1a',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#333',
    overflow: 'hidden',
  },
  bracketMatchBoxLive: {
    borderColor: '#e11900',
    backgroundColor: '#1a0808',
  },
  bracketMatchBoxUpcoming: {
    opacity: 0.5,
    borderColor: '#222',
  },
  bracketPlayerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 27,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
  },
  bracketPlayerRowWinner: {
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
  },
  bracketPlayerName: {
    color: '#888',
    fontSize: 11,
    flex: 1,
    marginRight: 4,
  },
  bracketPlayerNameWinner: {
    color: '#fff',
    fontWeight: '700',
  },
  bracketPlayerScore: {
    color: '#555',
    fontSize: 12,
    fontWeight: '700',
    minWidth: 16,
    textAlign: 'right',
  },
  bracketPlayerScoreWinner: {
    color: '#4ade80',
  },
  bracketMatchBoxDivider: {
    height: 1,
    backgroundColor: '#333',
  },
  bracketLiveIndicator: {
    position: 'absolute',
    top: 2,
    right: 2,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(225, 25, 0, 0.9)',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
  },
  bracketLiveDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#fff',
    marginRight: 3,
  },
  bracketLiveText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '900',
  },
  
  // Connector lines between rounds
  bracketConnectorsColumn: {
    width: 24,
    position: 'relative',
  },
  bracketConnectorWrapper: {
    height: 56,
    position: 'relative',
  },
  connectorLineH: {
    position: 'absolute',
    left: 0,
    top: 28, // Half of match height
    width: 12,
    height: 2,
    backgroundColor: '#444',
  },
  connectorLineV: {
    position: 'absolute',
    left: 11,
    top: 28,
    width: 2,
    backgroundColor: '#444',
  },
  connectorLineHOut: {
    position: 'absolute',
    left: 11,
    width: 13,
    height: 2,
    backgroundColor: '#444',
  },
  
  // Champion box
  bracketChampionWrapper: {
    marginLeft: 0,
  },
  bracketChampionBox: {
    width: 100,
    backgroundColor: '#1a1a0a',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#FCD000',
    padding: 12,
    alignItems: 'center',
  },
  bracketChampionLabel: {
    color: '#FCD000',
    fontSize: 9,
    fontWeight: '900',
    letterSpacing: 1,
    marginBottom: 4,
  },
  bracketChampionName: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },

  // Legacy bracket styles (keeping for compatibility)
  bracketGrid: {
    flexDirection: 'row',
    gap: 24,
  },
  bracketRoundCol: {
    width: 160,
  },
  bracketRoundTitle: {
    color: '#888',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    marginBottom: 12,
    textAlign: 'center',
  },
  bracketMatchesCol: {
    gap: 12,
  },
  bracketMatch: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: '#222',
  },
  bracketMatchLive: {
    borderColor: '#e11900',
    backgroundColor: '#1a0a0a',
  },
  bracketMatchUpcoming: {
    opacity: 0.5,
  },
  bracketMatchLiveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 6,
  },
  bracketMatchLiveText: {
    color: '#e11900',
    fontSize: 9,
    fontWeight: '900',
  },
  bracketMatchPlayer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  bracketMatchName: {
    color: '#aaa',
    fontSize: 12,
    flex: 1,
  },
  bracketMatchNameWinner: {
    color: '#fff',
    fontWeight: '700',
  },
  bracketMatchScore: {
    color: '#666',
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 8,
  },
  bracketMatchScoreWinner: {
    color: '#4ade80',
  },
  bracketMatchDivider: {
    height: 1,
    backgroundColor: '#333',
    marginVertical: 2,
  },

  // Match Detail Overlay
  matchDetailOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  matchDetailCard: {
    width: 320,
    backgroundColor: '#111',
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#333',
  },
  matchDetailClose: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#222',
    alignItems: 'center',
    justifyContent: 'center',
  },
  matchDetailTitle: {
    color: '#888',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
    textAlign: 'center',
    marginBottom: 20,
  },
  matchDetailPlayers: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  matchDetailPlayer: {
    alignItems: 'center',
    width: 100,
  },
  matchDetailAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#222',
    marginBottom: 8,
  },
  matchDetailAvatarWinner: {
    borderWidth: 2,
    borderColor: '#4ade80',
  },
  matchDetailName: {
    color: '#aaa',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  matchDetailNameWinner: {
    color: '#fff',
  },
  matchDetailScore: {
    color: '#666',
    fontSize: 32,
    fontWeight: '900',
    marginTop: 8,
  },
  matchDetailScoreWinner: {
    color: '#4ade80',
  },
  matchDetailVs: {
    color: '#444',
    fontSize: 14,
    fontWeight: '900',
  },
  watchMatchBtn: {
    backgroundColor: '#e11900',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 24,
  },
  watchMatchBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
});